import * as THREE from 'three';
import {OrbitControls} from 'three/examples/jsm/controls/OrbitControls';
import {
    RoomType,
    CabinetMesh,
    UserData,
    NewBasePosition,
} from 'components/customer/RoomPlanner/types';
import {
    setIsDragging,
    setSelectedProductValue,
    setIsRoomPlannerEditing,
    rotatingSet,
    setBackupProductValues,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {selectCabinet} from 'components/customer/RoomPlanner/lib/useCabinetSelect';
import {debouncedAutoSave as autoSave} from 'components/customer/RoomPlanner/lib/useAutoSave';
import ObjectNameMap from 'components/customer/RoomPlanner/constants/ObjectNameMap';
import {overlapValidator} from 'components/customer/RoomPlanner/lib/useCabinetOverlaps';
import {handleSnapping} from 'components/customer/RoomPlanner/helpers/cabinetSnapping';
import {isSnapCandidate} from 'components/customer/RoomPlanner/helpers/snapping/types';
import {productClamping} from 'components/customer/RoomPlanner/helpers/boundaries/clamping';
import {updateCabinetsCurrentWall} from 'components/customer/RoomPlanner/helpers/boundaries/walls';
import {checkCabinetOverlap} from 'components/customer/RoomPlanner/helpers/overlap';
import {
    getGroupWidth,
    getDirection,
    WALL_ROTATIONS,
} from 'components/customer/RoomPlanner/helpers/calculations';
import {reflushAfterRotation} from 'components/customer/RoomPlanner/helpers/reflushAfterRotation';
import {setPlaceholderFacingRotation} from 'components/customer/RoomPlanner/helpers/snapping/placeholderSnapping';
import {buildPlaceholderEditorParams} from 'components/customer/RoomPlanner/helpers/placeholder/editorParams';
import {
    isSideButtonClick,
    isAnySideButtonIntersected,
    trackSideButtonMouseMove,
    handleSideButtonClick,
    SideButtonRefs,
    SideButtonCallbacks,
} from 'components/customer/RoomPlanner/helpers/cabinetSideButtonHelpers';
import {AppState, store} from 'store/customer/storeSetup';
import {debounce, throttle} from 'lodash';
import {handleFillerSwitching} from 'components/customer/RoomPlanner/helpers/fillerSwitching';
import {
    applySnapState,
    clearSnapState,
} from 'components/customer/RoomPlanner/helpers/snapping/state';
import {
    shouldSkipDoorToggle,
    getNextDoorOrDrawerState,
} from 'components/customer/RoomPlanner/helpers/doorToggleLock';
import {isFootprintOnWalkableFloor} from 'components/customer/RoomPlanner/helpers/roomFloorFootprint';
import {
    clampGroupIntoRoomFootprint,
    getVisibleWallUnderCursor,
    getWallNormalAxis,
    getWallSurfaceBox,
    shouldJumpUpperToWall,
} from 'components/customer/RoomPlanner/helpers/wallProximity';
import {separateFromProducts} from 'components/customer/RoomPlanner/helpers/productSeparation';
import {isNonRectShapeProduct} from 'components/customer/RoomPlanner/helpers/shapeProducts';
import {isReselectingEditedCabinet} from 'components/customer/RoomPlanner/helpers/isReselectingEditedCabinet';
import CameraSetup from 'components/customer/RoomPlanner/lib/use3DRPCameraSetup';

const WALKABLE_FLOOR_TOLERANCE = 1;
const CANVAS_EVENTS_KEY = 'canvas';

const isUpperCabinetWallLocked = (cabinet: CabinetMesh): boolean =>
    !!cabinet.userData.isUpperProduct &&
    !cabinet.userData.isFillerProduct &&
    !cabinet.userData.isVerminProduct &&
    !cabinet.userData.isManualPanel &&
    !cabinet.userData.isPanelProduct;

const walkableFloorScratchDelta = new THREE.Vector3();
const walkableFloorScratchBox = new THREE.Box3();

const boxOf = (group: THREE.Object3D): THREE.Box3 =>
    walkableFloorScratchBox.setFromObject(group);

const isBoundsOnWalkableFloor = (bounds: THREE.Box3): boolean =>
    isFootprintOnWalkableFloor(bounds, WALKABLE_FLOOR_TOLERANCE);

// Clamp bounds span the L cut-out, so no guard upstream catches this.
const isPoseOnWalkableFloor = (
    group: THREE.Object3D,
    position: THREE.Vector3
): boolean =>
    isBoundsOnWalkableFloor(
        boxOf(group).translate(
            walkableFloorScratchDelta.subVectors(position, group.position)
        )
    );

export type ProductEventNames =
    | 'onSnap'
    | 'onOverlap'
    | 'onCabinetSelect'
    | 'onClick'
    | 'toggleDoor'
    | 'onToggleWireframe'
    | 'onClamp'
    | 'setGhostCabinet'
    | 'onCabinetOverlapsWhileDragging'
    | 'resetSegmentColor';

interface Intersection {
    distance: number;
    group: CabinetMesh;
    id: number | string;
}

type Events = {
    [k in keyof HTMLElementEventMap]?: (event: HTMLElementEventMap[k]) => void;
};

/**
 * Class representing product events.
 * This class handles the logic for managing product-related events.
 */
class ProductEvents {
    private isRoomPlannerEditing = false;
    public unsubscribe: () => void;
    private closeProductEditorMenu: (
        yesHandler?: () => void,
        isPlaceholder?: boolean
    ) => void;
    private lastSnapTimestamp = 0;

    private renderer: THREE.WebGLRenderer;
    private container: HTMLDivElement;
    private setDragPosition: React.Dispatch<
        React.SetStateAction<THREE.Vector3>
    >;
    private products: CabinetMesh[] = [];
    private searchParams: URLSearchParams;
    private updateSearchParams: React.Dispatch<
        React.SetStateAction<URLSearchParams>
    >;

    // Add context function property
    private setSelectedCabinetGroup?: React.Dispatch<
        React.SetStateAction<CabinetMesh | null>
    >;
    private placeholderListRef?: React.MutableRefObject<CabinetMesh[]>;
    private cabinetGroupListRef?: React.MutableRefObject<CabinetMesh[]>;
    private ghostCabinet: CabinetMesh = null;
    private ghostPanelCabinet: CabinetMesh = null;
    private wallJumpGhost: CabinetMesh = null;

    private isDragging = false;
    /** Cabinet deselected (Escape) mid-drag; mouseup still drops and saves it. */
    private deselectedDragCabinet: CabinetMesh | null = null;
    private clickedSprite: THREE.Sprite | null = null;
    private hoveredSprite: THREE.Sprite | null = null;
    private spriteWasClicked = false;

    // Side button click handling
    private sideButtonRefs: SideButtonRefs | null = null;
    private sideButtonCallbacks: SideButtonCallbacks | null = null;
    private sideButtonMouseDown: {x: number; y: number} | null = null;
    private sideButtonMouseMoved = false;
    private sideButtonWasClicked = false;

    private eventHandlers = new Map<string, Events>();
    private intersections: Intersection[] = [];
    private selectedCabinet: CabinetMesh | null = null;

    /** Set once the user deselects, so the URL hash no longer selects. */
    private isHashSelectionDismissed = false;
    private togglingCabinetIds = new Set<string>();
    private resetAttachPointerState: (() => void) | null = null;
    private boundDomElement: HTMLElement | null = null;

    // Performance optimizations
    private raycaster = new THREE.Raycaster();
    private mouse = new THREE.Vector2();
    private cachedArrowGroups: THREE.Object3D[] = [];
    private lastArrowCacheTime = 0;
    private arrowCacheTimeout = 100; // Cache arrow groups for 100ms
    private lastMousePosition = {x: 0, y: 0};
    private mouseMoveThreshold = 2; // Minimum pixel movement to trigger mouse move
    private isProcessingMouseMove = false;
    private pendingMouseMove: MouseEvent | null = null;
    private lastOverlapCheck = 0;
    private overlapCheckInterval = 50; // Check overlaps every 50ms
    private cachedWallMesh: THREE.Mesh<
        THREE.ExtrudeGeometry,
        THREE.MeshStandardMaterial
    > | null = null;
    private lastWallCacheTime = 0;
    private wallCacheTimeout = 500; // Cache wall mesh for 500ms

    // Panning state for both left-click and right-click panning
    private panningState = {
        left: {active: false, wasPanning: false, mouseMoved: false},
        right: {active: false, wasPanning: false, mouseMoved: false},
    };
    private isPreview = false;
    private isGeneratingReport = false;

    /**
     * Creates an instance of ProductEvents.
     * @param {Object} options - Configuration options for the product events.
     */
    constructor() {
        if (store) {
            this.unsubscribe = store.subscribe(
                this.onStateChange.bind(this) as () => void
            );
        }
    }

    /**
     * Handles state changes in the store.
     * This method is called whenever the store's state changes.
     */
    private onStateChange(): void {
        const state: AppState = store.getState();

        if (
            state.roomPlanner.isRoomPlannerEditing !== this.isRoomPlannerEditing
        ) {
            this.isRoomPlannerEditing = state.roomPlanner.isRoomPlannerEditing;
        }
    }

    /**
     * Sets whether the room planner is in preview mode.
     * @param {boolean} isPreview - True if in preview mode, false otherwise.
     * @return {this} - Returns the current instance for method chaining.
     */
    public setIsPreview(isPreview: boolean) {
        this.isPreview = isPreview;
        return this;
    }

    /**
     * Sets whether the room planner is rendering for the PDF report.
     * Overlap highlights are suppressed so they don't end up in snapshots.
     * @param {boolean} isGeneratingReport - True while generating the report.
     * @return {this} - Returns the current instance for method chaining.
     */
    public setIsGeneratingReport(isGeneratingReport: boolean) {
        this.isGeneratingReport = isGeneratingReport;
        return this;
    }

    /**
     * Gets cached arrow groups to avoid repeated filtering.
     * @param {THREE.Scene} scene - The scene to get arrow groups from.
     * @param {string | number} activeGroupId - The active group ID.
     * @return {THREE.Object3D[]} - The cached arrow groups.
     */
    private getCachedArrowGroups(
        scene: THREE.Scene,
        activeGroupId: string | number
    ): THREE.Object3D[] {
        const now = Date.now();
        if (now - this.lastArrowCacheTime > this.arrowCacheTimeout) {
            this.cachedArrowGroups = scene.children.filter(
                (child) =>
                    child.name === 'cabinetMovementArrows' &&
                    (child.userData?.id === activeGroupId || child.visible)
            );
            this.lastArrowCacheTime = now;
        }
        return this.cachedArrowGroups;
    }

    /**
     * Returns raycaster hits on visible reposition arrows for the selected cabinet.
     * @param {THREE.Raycaster} raycaster - The raycaster to use.
     * @param {THREE.Scene} scene - The scene to search for arrows.
     * @param {CabinetMesh} selectedCabinet - The selected cabinet to search for arrows.
     * @return {THREE.Intersection[]} - The intersections.
     */
    private getMovementArrowIntersects(
        raycaster: THREE.Raycaster,
        scene: THREE.Scene,
        selectedCabinet: CabinetMesh
    ): THREE.Intersection[] {
        const arrowGroups = scene.children.filter(
            (child) =>
                child.name === 'cabinetMovementArrows' &&
                child.visible === true &&
                child.userData.id === selectedCabinet.userData.id
        );

        if (arrowGroups.length === 0) {
            return [];
        }

        const visibleArrows: THREE.Object3D[] = [];
        arrowGroups.forEach((arrowGroup) => {
            arrowGroup.traverse((child) => {
                if (child.userData?.isArrow && child.visible !== false) {
                    visibleArrows.push(child);
                }
            });
        });

        if (visibleArrows.length === 0) {
            return [];
        }

        return raycaster.intersectObjects(visibleArrows, true);
    }

    /**
     * Resolves a raycast hit to the parent movement arrow mesh.
     * @param {THREE.Object3D} object - The intersected object.
     * @return {THREE.Object3D | null} The arrow mesh, if found.
     */
    private resolveMovementArrowMesh(
        object: THREE.Object3D
    ): THREE.Object3D | null {
        let current: THREE.Object3D | null = object;
        while (current) {
            if (current.userData?.isArrow) {
                return current;
            }
            current = current.parent;
        }
        return null;
    }

    /**
     * Returns the single reposition arrow closest to the camera under the cursor.
     * @param {THREE.Raycaster} raycaster - The raycaster to use.
     * @param {THREE.Scene} scene - The scene to search for arrows.
     * @param {CabinetMesh} selectedCabinet - The selected cabinet.
     * @return {THREE.Object3D | null} The closest arrow mesh, if any.
     */
    private getClosestMovementArrowMesh(
        raycaster: THREE.Raycaster,
        scene: THREE.Scene,
        selectedCabinet: CabinetMesh
    ): THREE.Object3D | null {
        const intersects = this.getMovementArrowIntersects(
            raycaster,
            scene,
            selectedCabinet
        );

        for (const intersect of intersects) {
            const arrowMesh = this.resolveMovementArrowMesh(intersect.object);
            if (arrowMesh && arrowMesh.visible !== false) {
                return arrowMesh;
            }
        }

        return null;
    }

    /**
     * When reposition mode is on, movement arrows take priority over distance labels.
     * @param {THREE.Raycaster} raycaster - The raycaster to use.
     * @param {THREE.Scene} scene - The scene to search for arrows.
     * @param {CabinetMesh | null} selectedCabinet - The selected cabinet to search for arrows.
     * @return {boolean} - True if the movement arrow should be prioritized.
     */
    private shouldPrioritizeMovementArrow(
        raycaster: THREE.Raycaster,
        scene: THREE.Scene,
        selectedCabinet: CabinetMesh | null
    ): boolean {
        if (!selectedCabinet?.userData.isMovementMode) {
            return false;
        }

        return (
            this.getMovementArrowIntersects(raycaster, scene, selectedCabinet)
                .length > 0
        );
    }

    /**
     * Gets cached wall mesh to avoid repeated searching.
     * @param {THREE.Scene} scene - The scene to get wall mesh from.
     * @return {THREE.Mesh<THREE.ExtrudeGeometry, THREE.MeshStandardMaterial> | null} - The cached wall mesh.
     */
    private getCachedWallMesh(
        scene: THREE.Scene
    ): THREE.Mesh<THREE.ExtrudeGeometry, THREE.MeshStandardMaterial> | null {
        const now = Date.now();
        if (now - this.lastWallCacheTime > this.wallCacheTimeout) {
            this.cachedWallMesh = scene.children.find(
                (ch) =>
                    ch.name === ObjectNameMap.rectangularWall ||
                    ch.name === ObjectNameMap.lShapeWall
            ) as THREE.Mesh<THREE.ExtrudeGeometry, THREE.MeshStandardMaterial>;
            this.lastWallCacheTime = now;
        }
        return this.cachedWallMesh;
    }

    /**
     * Resets panning state for a specific button or all buttons.
     * @param {'left' | 'right' | 'all'} button - Which button's state to reset
     */
    private resetPanningState(button: 'left' | 'right' | 'all'): void {
        if (button === 'all' || button === 'left') {
            this.panningState.left = {
                active: false,
                wasPanning: false,
                mouseMoved: false,
            };
        }
        if (button === 'all' || button === 'right') {
            this.panningState.right = {
                active: false,
                wasPanning: false,
                mouseMoved: false,
            };
        }
    }

    /**
     * Handles panning end on mouseup.
     * @param {'left' | 'right'} button - Which button's panning to end
     * @param {OrbitControls} controls - OrbitControls to re-enable
     */
    private endPanning(
        button: 'left' | 'right',
        controls: OrbitControls
    ): void {
        const state =
            button === 'left'
                ? this.panningState.left
                : this.panningState.right;
        if (state.active) {
            if (state.mouseMoved) {
                state.wasPanning = true;
            }
            state.active = false;
            state.mouseMoved = false;
            controls.enabled = true;
        }
    }

    /**
     * Checks if panning prevented the action and resets the wasPanning flag.
     * @param {'left' | 'right'} button - Which button's state to check
     * @return {boolean} True if panning was active and should prevent action
     */
    private checkAndResetWasPanning(button: 'left' | 'right'): boolean {
        const state =
            button === 'left'
                ? this.panningState.left
                : this.panningState.right;
        if (state.wasPanning) {
            state.wasPanning = false;
            return true;
        }
        return false;
    }

    /**
     * Sets selected cabinet's currentWall based on the wall under the mouse pointer.
     * Upper cabinets keep the wall they are mounted on; jump-on-drag is what
     * moves them, so a click through a hidden wall must not steal attachment.
     * @private
     * @param {THREE.Scene} scene - The scene to search for walls.
     * @param {THREE.Raycaster} raycaster - Configured raycaster (mouse+camera).
     */
    private setCurrentWallFromMouse(
        scene: THREE.Scene,
        raycaster: THREE.Raycaster
    ): void {
        if (!this.selectedCabinet) return;
        if (isUpperCabinetWallLocked(this.selectedCabinet)) return;

        const wallGroup = scene.children.find(
            (ch) =>
                ch.name === ObjectNameMap.rectangularWall ||
                ch.name === ObjectNameMap.lShapeWall
        );

        if (!wallGroup) return;
        raycaster.layers.set(0);
        const targetWall = getVisibleWallUnderCursor(raycaster, wallGroup);
        if (!targetWall) return;

        this.selectedCabinet.userData.currentWall = targetWall as THREE.Mesh<
            THREE.ExtrudeGeometry,
            THREE.MeshStandardMaterial
        >;
    }

    /**
     * Optimized mouse position update with threshold checking.
     * @param {MouseEvent} event - The mouse event.
     * @param {THREE.OrthographicCamera} camera - The camera instance.
     * @return {boolean} - True if mouse position changed significantly.
     */
    private updateMousePosition(
        event: MouseEvent,
        camera: THREE.OrthographicCamera
    ): boolean {
        const dimension = this.container.getBoundingClientRect();
        const newX =
            ((event.clientX - dimension.left) / dimension.width) * 2 - 1;
        const newY =
            (-(event.clientY - dimension.top) / dimension.height) * 2 + 1;

        // Check if mouse moved significantly
        const deltaX = Math.abs(newX - this.lastMousePosition.x);
        const deltaY = Math.abs(newY - this.lastMousePosition.y);

        if (
            deltaX < this.mouseMoveThreshold / dimension.width &&
            deltaY < this.mouseMoveThreshold / dimension.height
        ) {
            return false;
        }

        this.mouse.x = newX;
        this.mouse.y = newY;
        this.lastMousePosition.x = newX;
        this.lastMousePosition.y = newY;

        this.raycaster.setFromCamera(this.mouse, camera);
        return true;
    }

    /**
     * Debounced overlap check to reduce performance impact.
     * @param {CabinetMesh} cabinet - The cabinet to check.
     * @param {THREE.Scene} scene - The scene instance.
     */
    private debouncedOverlapCheck = debounce(
        (
            cabinet: CabinetMesh,
            newBasePosition?: NewBasePosition,
            roomType?: RoomType
        ) => {
            const now = Date.now();
            if (now - this.lastOverlapCheck < this.overlapCheckInterval) {
                return;
            }
            this.lastOverlapCheck = now;

            overlapValidator.cabinet(
                cabinet,
                this.products,
                (value) => {
                    // Only set red here. Clearing is owned by the drag path
                    // (candidate/target overlap), otherwise this fires on the
                    // safe current pose and immediately wipes overlap feedback.
                    if (value) {
                        this.triggerEvents(cabinet, 'onOverlap', true);
                    }
                },
                newBasePosition,
                roomType
            );
        },
        16
    );

    /**
     * Sets the WebGL renderer for the product events.
     * @param {THREE.WebGLRenderer} renderer - The WebGL renderer to be set.
     * @return {this} - Returns the current instance for method chaining.
     */
    public setRenderer(renderer: THREE.WebGLRenderer) {
        this.renderer = renderer;
        return this;
    }

    /**
     * Sets the container element for the product events.
     * @param {HTMLDivElement} container - The container element to be set.
     * @return {this} - Returns the current instance for method chaining.
     */
    public setContainer(container: HTMLDivElement) {
        this.container = container;
        return this;
    }

    /**
     * Sets the placeholder list ref used when building the products array.
     * @param {React.MutableRefObject<CabinetMesh[]>} ref - Placeholder mesh list ref.
     * @return {this} - Returns the current instance for method chaining.
     */
    public setPlaceholderListRef(ref: React.MutableRefObject<CabinetMesh[]>) {
        this.placeholderListRef = ref;
        return this;
    }

    /**
     * Sets the cabinet group list ref for product event lookups.
     * @param {React.MutableRefObject<CabinetMesh[]>} ref - Cabinet mesh list ref.
     * @return {this} - Returns the current instance for method chaining.
     */
    public setCabinetGroupListRef(ref: React.MutableRefObject<CabinetMesh[]>) {
        this.cabinetGroupListRef = ref;
        return this;
    }

    /**
     * Live cabinet + placeholder meshes from the list refs (current world transforms).
     * @return {CabinetMesh[]} Cabinets followed by placeholders
     */
    public getLayoutMeshes(): CabinetMesh[] {
        const cabinets = this.cabinetGroupListRef?.current ?? [];
        const placeholders = this.placeholderListRef?.current ?? [];
        // Prefer live refs whenever they are wired, including empty lists
        // after the last placeholder/cabinet is deleted.
        if (this.cabinetGroupListRef || this.placeholderListRef) {
            return cabinets.concat(placeholders);
        }
        return this.products ?? [];
    }

    /**
     * Sets the products for the product events, including placeholders.
     * @param {CabinetMesh[]} products - The cabinet products to be set.
     * @return {this} - Returns the current instance for method chaining.
     */
    public setProducts(products: CabinetMesh[]) {
        const cabinets = products ?? [];
        const placeholders = this.placeholderListRef?.current ?? [];
        // Keep the cabinet list itself when nothing is appended. Click tests
        // and callers identify that array by reference.
        this.products =
            placeholders.length > 0 ? cabinets.concat(placeholders) : cabinets;
        return this;
    }

    /**
     * Sets the drag position for the product events.
     * @param {React.Dispatch<React.SetStateAction<THREE.Vector3>>} setDragPosition - The function to set the drag position.
     * @return {this} - Returns the current instance for method chaining.
     */
    public setSetDragPosition(
        setDragPosition: React.Dispatch<React.SetStateAction<THREE.Vector3>>
    ) {
        this.setDragPosition = setDragPosition;
        return this;
    }

    /**
     * Sets the search parameters for the product events.
     * @param {URLSearchParams} searchParams - The search parameters to be set.
     * @return {this} - Returns the current instance for method chaining.
     */
    public setSearchParams(searchParams: URLSearchParams) {
        this.searchParams = searchParams;
        return this;
    }

    /**
     * Drop `selected`/`index` on the live copy and in the URL.
     * Used by Esc / floor-click deselect, where no other navigation is
     * already rewriting the query. Editor close must not use this — a
     * second URL write from a stale snapshot put the editor params back
     * and left the session open until a second click.
     * @return {void}
     */
    public clearSelectionFromUrl() {
        if (!this.searchParams) {
            return;
        }
        if (
            !this.searchParams.has('selected') &&
            !this.searchParams.has('index')
        ) {
            return;
        }
        const next = new URLSearchParams(this.searchParams);
        next.delete('selected');
        next.delete('index');
        this.searchParams = next;
        this.updateSearchParams?.(next);
    }

    /**
     * Drop `selected`/`index` on the live copy used by overlap revalidation.
     * Mutates in place so this stays the same object React Router owns —
     * replacing it made the next click write `selected` onto a detached
     * copy that the planner then overwrote, so the cabinet never stayed
     * selected.
     * @return {void}
     */
    public dropSelectionParams() {
        this.searchParams?.delete('selected');
        this.searchParams?.delete('index');
    }

    /**
     * True while the cabinet or placeholder editor sidebar should stay open.
     * @return {boolean}
     */
    private isEditSidebarOpen(): boolean {
        return (
            this.searchParams?.get('preventReset') === 'true' ||
            Boolean(this.searchParams?.get('placeholderEdit')) ||
            Boolean(this.searchParams?.get('placeholderFace'))
        );
    }

    /**
     * Sets the function to update the search parameters.
     * @param {React.Dispatch<React.SetStateAction<URLSearchParams>>} updateSearchParams - The function to update the search parameters.
     * @return {this} - Returns the current instance for method chaining.
     */
    public setUpdateSearchParams(
        updateSearchParams: React.Dispatch<
            React.SetStateAction<URLSearchParams>
        >
    ) {
        this.updateSearchParams = updateSearchParams;
        return this;
    }

    /**
     * Sets the close product editor menu function.
     * @param {Function} closeProductEditorMenu - The function to close the product editor menu.
     * @return {this} - Returns the current instance for method chaining.
     */
    public setCloseProductEditorMenu(
        closeProductEditorMenu: (
            successHandler?: () => void,
            isPlaceholder?: boolean
        ) => void
    ) {
        this.closeProductEditorMenu = closeProductEditorMenu;
        return this;
    }

    /**
     * Whether a product is currently being edited and has unsaved changes.
     * Any caller that is about to switch the selected/edited product should
     * check this first and route through confirmDiscardProductChanges if true.
     * @return {boolean}
     */
    public hasUnsavedProductChanges(): boolean {
        const selectedProductUpdated =
            store.getState().productData.productUpdated;
        const editingProduct =
            (this.searchParams?.has('cabinetId') ?? false) ||
            (this.searchParams?.has('placeholderEdit') ?? false);

        return editingProduct && selectedProductUpdated;
    }

    /**
     * Shows the unsaved changes dialog registered by useRoomPlanner. yesHandler
     * runs (and the edited product's dimensions are reverted) only if the user
     * confirms discarding.
     * @param {Function} [yesHandler] - Runs the pending product switch on confirm.
     * @param {boolean} [isPlaceholder] - Skip cabinet restore when leaving a placeholder editor.
     */
    public confirmDiscardProductChanges(
        yesHandler?: () => void,
        isPlaceholder = false
    ) {
        if (typeof this.closeProductEditorMenu == 'function') {
            this.closeProductEditorMenu(yesHandler, isPlaceholder);
        }
    }

    /**
     * Sets the setSelectedCabinetGroup function from RoomPlannerContext.
     * @param {React.Dispatch<React.SetStateAction<CabinetMesh | null>>} setSelectedCabinetGroup - The function to set selected cabinet group.
     * @return {this} - Returns the current instance for method chaining.
     */
    public setSetSelectedCabinetGroup(
        setSelectedCabinetGroup: React.Dispatch<
            React.SetStateAction<CabinetMesh | null>
        >
    ) {
        this.setSelectedCabinetGroup = setSelectedCabinetGroup;
        return this;
    }

    /**
     * Registers a callback that the context will call after undo/redo.
     * Used to run product-event side effects (e.g. reset panning, trigger synthetic mousedown).
     */
    /**
     * Sets the selected cabinet group.
     * @param {CabinetMesh | null} cabinetGroup - The cabinet group to set.
     * @return {this} - Returns the current instance for method chaining.
     */
    public setSelectedGroup(cabinetGroup: CabinetMesh | null) {
        this.selectedCabinet = cabinetGroup;
        // Reset movement mode for all cabinets when changing selection, excluding the new selection
        this.resetMovementModeForAllCabinets(cabinetGroup);

        if (this.setSelectedCabinetGroup) {
            this.setSelectedCabinetGroup(cabinetGroup);
        }
        return this;
    }

    /**
     * Drop the internal selection if it matches the given mesh (e.g. after delete).
     * @param {CabinetMesh} mesh Mesh that was removed
     * @return {void}
     */
    public clearSelectedIf(mesh: CabinetMesh) {
        if (this.selectedCabinet?.uuid === mesh.uuid) {
            this.selectedCabinet = null;
            this.resetPointerInteraction();
        }
    }

    /**
     * Clear drag/click flags so the next canvas click can raycast again.
     * Needed when delete happens on a UI button (canvas never gets mouseup).
     * @return {void}
     */
    public resetPointerInteraction() {
        this.isDragging = false;
        this.intersections = [];
        this.spriteWasClicked = false;
        this.sideButtonWasClicked = false;
        this.resetAttachPointerState?.();
        store.dispatch(setIsDragging(false));
    }

    /**
     * Clears the in-memory selection and its highlight, as clicking empty
     * floor does. Without this, revalidateOverlaps still treats the cabinet
     * as selected and re-applies its highlight.
     * @return {this} - Returns the current instance for method chaining.
     */
    public deselectCabinet() {
        // Escape mid-drag leaves the cabinet where it was dragged to, so
        // mouseup must still drop and save it.
        if (this.isDragging) {
            this.deselectedDragCabinet = this.selectedCabinet;
        }
        this.clearCabinetHighlight(this.selectedCabinet);
        this.selectedCabinet = null;
        // The URL hash deep-link must not re-select after an explicit
        // deselect (nothing clears the hash itself).
        this.isHashSelectionDismissed = true;
        return this;
    }

    /**
     * Selects a cabinet programmatically (e.g. from URL hash on initial load)
     * and applies the same highlight used by click selection.
     * @param {CabinetMesh} cabinet - The cabinet mesh to select and highlight.
     * @return {this} - Returns the current instance for method chaining.
     */
    public highlightCabinet(cabinet: CabinetMesh) {
        if (this.selectedCabinet?.uuid !== cabinet.uuid) {
            this.clearCabinetHighlight(this.selectedCabinet);
        }

        this.selectedCabinet = cabinet;
        this.resetMovementModeForAllCabinets(cabinet);

        if (this.setSelectedCabinetGroup) {
            this.setSelectedCabinetGroup(cabinet);
        }

        if (cabinet.userData?.values) {
            store.dispatch(setSelectedProductValue(cabinet.userData.values));
        }

        const camera = CameraSetup.camera;
        if (camera && this.renderer) {
            selectCabinet(cabinet, camera, this.renderer);
        }

        return this;
    }

    /**
     * Clears the in-memory selection and its outline. Must run whenever the
     * React selection state is cleared, otherwise checkCabinetIfSelected keeps
     * reporting the old cabinet as selected and revalidateOverlaps re-applies
     * the selection outline after it was reset.
     * @return {this} - Returns the current instance for method chaining.
     */
    public clearSelectedCabinet() {
        this.clearCabinetHighlight(this.selectedCabinet);
        this.selectedCabinet = null;
        return this;
    }

    /**
     * Resets movement mode for all cabinets in the products array.
     * This ensures that when switching between cabinets, movement mode is turned off.
     * @param {CabinetMesh | null} excludeCabinet - Cabinet to exclude from reset (optional)
     * @private
     */
    private resetMovementModeForAllCabinets(
        excludeCabinet?: CabinetMesh | null
    ) {
        const meshes = this.getLayoutMeshes();
        meshes.forEach((cabinet) => {
            // Skip the excluded cabinet (usually the currently selected one)
            if (excludeCabinet && cabinet.uuid === excludeCabinet.uuid) {
                return;
            }

            if (cabinet.userData.isMovementMode) {
                cabinet.userData.isMovementMode = false;

                // Reset hover states for arrows
                const userDataWithArrowGroup = cabinet.userData as UserData & {
                    arrowGroup?: THREE.Group;
                };

                if (userDataWithArrowGroup.arrowGroup) {
                    const arrowGroup = userDataWithArrowGroup.arrowGroup;

                    if (arrowGroup.children) {
                        arrowGroup.children.forEach((arrow: THREE.Object3D) => {
                            if (
                                arrow.userData &&
                                arrow.userData.isArrow &&
                                arrow.userData.isHovered
                            ) {
                                if (
                                    typeof arrow.userData.onHoverEnd ===
                                    'function'
                                ) {
                                    (
                                        arrow.userData as {
                                            onHoverEnd: () => void;
                                        }
                                    ).onHoverEnd();
                                }
                                arrow.userData.isHovered = false;
                            }
                        });
                    }
                }

                // Call userData methods to update UI state
                const userData = cabinet.userData as {
                    setActiveState?: (isActive: boolean) => void;
                    setDistanceMeasurementsActive?: (isActive: boolean) => void;
                };

                if (typeof userData?.setActiveState === 'function') {
                    userData.setActiveState(false);
                }

                if (
                    typeof userData?.setDistanceMeasurementsActive ===
                    'function'
                ) {
                    userData.setDistanceMeasurementsActive(false);
                }
            }
        });
    }

    /**
     * Updates the currentWall property for all cabinets
     * @param {THREE.Scene} scene - The scene containing the walls
     * @param {string} roomType - The type of room (RECTANGULAR or LSHAPED)
     */
    public updateAllCabinetsCurrentWall(scene: THREE.Scene) {
        updateCabinetsCurrentWall(this.products, scene);
    }

    /**
     * cancels any pending auto-save operations and clears event listeners.
     */
    public destroy() {
        this.unsubscribe();
        autoSave.cancel();
    }

    /**
     * Triggers events for the specified group and event name.
     * @param {CabinetMesh} group - The group to trigger events on.
     * @param {ProductEventNames} eventName - The name of the event to trigger.
     * @param {T} params - The parameters to pass to the event handler.
     */
    public triggerEvents<T>(
        group: CabinetMesh,
        eventName: ProductEventNames,
        params: T
    ) {
        if (group) {
            group.traverse((child) => {
                const eventHandler = (child.userData as unknown as UserData)[
                    eventName as keyof UserData
                ];
                if (typeof eventHandler === 'function') {
                    (eventHandler as (params: T) => void)(params);
                }
            });
        }
    }

    /**
     * Triggers events for the specified group and event name.
     * @param {CabinetMesh} group - The group to trigger events on.
     * @param {ProductEventNames} eventName - The name of the event to trigger.
     * @param {T} params - The parameters to pass to the event handler.
     */
    public async triggerEventsAsync<T>(
        group: CabinetMesh,
        eventName: ProductEventNames,
        params: T
    ) {
        if (!group) return;

        const tasks: Promise<unknown>[] = [];
        group.traverse((child) => {
            const eventHandler = (child.userData as unknown as UserData)[
                eventName as keyof UserData
            ];

            if (typeof eventHandler === 'function') {
                const result = (eventHandler as (args: T) => unknown)(params);
                if (
                    result &&
                    typeof (result as PromiseLike<unknown>).then === 'function'
                ) {
                    tasks.push(result as Promise<unknown>);
                }
            }
        });

        if (tasks.length) {
            await Promise.allSettled(tasks);
        }
    }

    /**
     * Toggles the cabinet door or drawer state with interaction locking.
     * @param {CabinetMesh} selectedCabinet - The cabinet to toggle.
     * @return {Promise<void>} Resolves when the toggle operation completes.
     */
    private async toggleDoorWithLock(selectedCabinet: CabinetMesh) {
        const product = selectedCabinet.userData.values;
        const noInteractions = selectedCabinet?.userData.noInteractions;
        const cabinetId = selectedCabinet.uuid;

        if (
            shouldSkipDoorToggle({
                noInteractions,
                isToggling: this.togglingCabinetIds.has(cabinetId),
            })
        ) {
            return;
        }

        const nextDoorState = getNextDoorOrDrawerState(
            selectedCabinet.userData.isDoorOrDrawerOpen
        );

        selectedCabinet.userData.isDoorOrDrawerOpen = nextDoorState;
        this.togglingCabinetIds.add(cabinetId);

        try {
            await this.triggerEventsAsync(selectedCabinet, 'toggleDoor', {
                doorGap: product.cabinet_door_gap,
                isDoorOrDrawerOpen: nextDoorState,
            });
        } finally {
            this.togglingCabinetIds.delete(cabinetId);
        }
    }

    /**
     * Renders the ghost cabinet.
     * @param {boolean} hasOverlap - Whether the cabinet overlaps.
     * @param {boolean} isUpperProduct - Whether the cabinet is an upper product.
     * @param {CabinetMesh} selectedCabinet - The cabinet group to render.
     * @param {THREE.Scene} scene - The scene to render the ghost cabinet in.
     * @param {THREE.Vector3} targetPos - The target position of the cabinet.
     * @param {number} positionY - The Y position of the cabinet.
     */
    private renderGhostCabinet(
        hasOverlap: boolean,
        isUpperProduct: boolean,
        selectedCabinet: CabinetMesh,
        scene: THREE.Scene,
        targetPos: THREE.Vector3,
        positionY: number
    ) {
        // Create or remove ghost cabinet based on overlap
        if (
            hasOverlap &&
            !selectedCabinet.userData.isSnapMode &&
            !selectedCabinet.userData?.isPlaceholder
        ) {
            if (!this.ghostCabinet) {
                const ghostCabinet = selectedCabinet.clone();
                this.ghostCabinet = ghostCabinet;
                this.ghostCabinet.traverse((child) => {
                    if (child instanceof THREE.Mesh) {
                        child.userData.setGhostCabinet = (visible: boolean) => {
                            child.visible = visible;
                        };
                    }
                });
                scene.add(this.ghostCabinet);
                this.triggerEvents(ghostCabinet, 'setGhostCabinet', false);
                this.triggerEvents(
                    ghostCabinet,
                    'onCabinetOverlapsWhileDragging',
                    true
                );
            }

            // Update ghost cabinet position. Upper products keep their
            // mounting height and wall rotation while the ghost follows the
            // attempted drag target (depth-frozen for lateral wall drags).
            targetPos.y = positionY;
            this.ghostCabinet.position.copy(targetPos);
            this.ghostCabinet.rotation.z = selectedCabinet.rotation.z;
        } else {
            // Remove ghost cabinet if there's no overlap
            if (this.ghostCabinet) {
                scene.remove(this.ghostCabinet);
                this.ghostCabinet = null;
            }
        }
    }

    /**
     * Renders the ghost cabinet.
     * @param {boolean} isUpperProduct - Whether the cabinet is an upper product.
     * @param {CabinetMesh} selectedCabinet - The cabinet group to render.
     * @param {THREE.Scene} scene - The scene to render the ghost cabinet in.
     * @param {THREE.Vector3} targetPos - The target position of the cabinet.
     * @param {number} positionY - The Y position of the cabinet.
     */
    private renderGhostPanelCabinet(
        isUpperProduct: boolean,
        selectedCabinet: CabinetMesh,
        scene: THREE.Scene,
        targetPos: THREE.Vector3,
        positionY: number
    ) {
        // Create or remove ghost cabinet based on overlap
        if (!this.ghostPanelCabinet) {
            const ghostCabinet = selectedCabinet.clone();
            this.ghostPanelCabinet = ghostCabinet;
            this.ghostPanelCabinet.traverse((child) => {
                if (child instanceof THREE.Mesh) {
                    child.userData.setGhostCabinet = (visible: boolean) => {
                        child.visible = visible;
                    };
                }
            });
            scene.add(this.ghostPanelCabinet);
            this.triggerEvents(ghostCabinet, 'setGhostCabinet', false);
        }
        this.ghostPanelCabinet.userData.isGhostPanel = true;
        // Update ghost cabinet position
        if (!isUpperProduct) {
            targetPos.y = positionY;
            this.ghostPanelCabinet.position.copy(targetPos);
        }
    }

    /**
     * Removes a drag's ghost previews, moving the cabinet onto the panel
     * snap ghost's position if one is showing.
     * @param {CabinetMesh} cabinet - The cabinet being dropped.
     * @param {THREE.Scene} scene - The scene holding the ghosts.
     */
    private dropDragGhosts(cabinet: CabinetMesh, scene: THREE.Scene) {
        if (this.ghostPanelCabinet) {
            cabinet.position.copy(this.ghostPanelCabinet.position);
            scene.remove(this.ghostPanelCabinet);
            this.ghostPanelCabinet = null;
        }

        if (this.ghostCabinet) {
            scene.remove(this.ghostCabinet);
            this.ghostCabinet = null;
        }
        this.removeWallJumpGhost(scene);
    }

    /**
     * Removes the wall-jump ghost preview from the scene.
     * @param {THREE.Scene} scene - The scene the ghost was added to.
     */
    private removeWallJumpGhost(scene: THREE.Scene) {
        if (this.wallJumpGhost) {
            scene.remove(this.wallJumpGhost);
            this.wallJumpGhost = null;
        }
    }

    /**
     * While dragging an upper cabinet, previews a ghost pinned to the wall
     * under the cursor and jumps the cabinet onto that wall when there is
     * space for it. The ghost stays visible as long as the target spot is
     * blocked by another product.
     * @param {CabinetMesh} cabinetToMove - The cabinet being dragged.
     * @param {THREE.Mesh} wall - The room wall group used for clamping.
     * @param {THREE.OrthographicCamera} camera - The active camera.
     * @param {THREE.Scene} scene - The scene to render the ghost in.
     * @param {THREE.Vector3} rawDragPosition - Unfrozen mouse drag target.
     * @param {string} roomType - The current room type.
     * @return {'none' | 'preview' | 'jumped'} 'jumped' when the cabinet
     * moved to the new wall, 'preview' while the ghost is showing on a
     * blocked spot, 'none' when the cursor is on the current wall.
     */
    private handleUpperWallJump(
        cabinetToMove: CabinetMesh,
        wall: THREE.Mesh<THREE.ExtrudeGeometry, THREE.MeshStandardMaterial>,
        camera: THREE.OrthographicCamera,
        scene: THREE.Scene,
        rawDragPosition: THREE.Vector3,
        roomType: string
    ): 'none' | 'preview' | 'jumped' {
        this.raycaster.layers.set(0);
        const targetWall = getVisibleWallUnderCursor(
            this.raycaster,
            wall,
            rawDragPosition
        );

        if (
            !targetWall ||
            !(targetWall.name in WALL_ROTATIONS) ||
            !shouldJumpUpperToWall(
                cabinetToMove.userData.currentWall,
                targetWall,
                rawDragPosition
            )
        ) {
            this.removeWallJumpGhost(scene);
            return 'none';
        }

        const newRotation =
            WALL_ROTATIONS[targetWall.name as keyof typeof WALL_ROTATIONS];

        if (!this.wallJumpGhost) {
            const ghost = cabinetToMove.clone();
            ghost.traverse((child) => {
                if (child instanceof THREE.Mesh) {
                    child.userData.setGhostCabinet = (visible: boolean) => {
                        child.visible = visible;
                    };
                }
            });
            scene.add(ghost);
            this.wallJumpGhost = ghost;
            this.triggerEvents(ghost, 'setGhostCabinet', false);
        }

        const ghost = this.wallJumpGhost;
        // clone() deep-copies userData via JSON, which breaks the mesh
        // reference productClamping reads; restore by reference each frame.
        ghost.userData = {
            ...cabinetToMove.userData,
            currentWall: targetWall,
        };
        ghost.position.copy(cabinetToMove.position);
        ghost.updateMatrixWorld(true);

        // Same centre-preserving auto-rotate productClamping uses for floor
        // cabinets, so the upper turns to face the hovered wall as it jumps.
        const centerBefore = new THREE.Box3()
            .setFromObject(ghost)
            .getCenter(new THREE.Vector3());
        if (ghost.userData?.isPlaceholder) {
            setPlaceholderFacingRotation(ghost, newRotation);
        } else {
            ghost.rotation.z = newRotation;
        }
        ghost.updateMatrixWorld(true);
        const centerAfter = new THREE.Box3()
            .setFromObject(ghost)
            .getCenter(new THREE.Vector3());
        ghost.position.sub(centerAfter.sub(centerBefore));
        ghost.updateMatrixWorld(true);

        const pinned = productClamping(
            ghost,
            wall,
            camera,
            this.mouse,
            rawDragPosition,
            roomType,
            true,
            true
        );
        ghost.position.set(
            pinned.clamped.x,
            cabinetToMove.position.y,
            pinned.clamped.z
        );
        ghost.updateMatrixWorld(true);

        // Geometry flush against the real wall surface (same helper used on
        // rotate). forceFlush zeros any leftover wallOffset gap from clamping.
        const jumpBox = new THREE.Box3().setFromObject(ghost);
        const flushed = reflushAfterRotation(ghost, targetWall, jumpBox, {
            forceFlush: true,
        });
        if (flushed) {
            ghost.position.set(flushed.x, cabinetToMove.position.y, flushed.z);
            ghost.updateMatrixWorld(true);
        }

        const overlaps = checkCabinetOverlap(
            ghost,
            ghost.position,
            this.products
        );

        if (overlaps) {
            // Blocked: keep the ghost as a preview on the target wall.
            return 'preview';
        }

        // Space available: jump the cabinet onto the new wall.
        if (cabinetToMove.userData?.isPlaceholder) {
            setPlaceholderFacingRotation(cabinetToMove, newRotation);
        } else {
            cabinetToMove.rotation.z = newRotation;
        }
        cabinetToMove.userData.currentWall = targetWall;
        const newDirection = getDirection(newRotation);
        cabinetToMove.userData.currentDirection =
            newDirection === 'normal'
                ? 'front'
                : newDirection === 'left'
                  ? 'right'
                  : newDirection === 'right'
                    ? 'left'
                    : newDirection;
        cabinetToMove.position.set(
            ghost.position.x,
            cabinetToMove.position.y,
            ghost.position.z
        );
        cabinetToMove.updateMatrixWorld(true);

        // Toggle the rotating flag so overlays/measurements rebuild,
        // mirroring what productClamping does on auto-rotation.
        const isRotating = store.getState().roomPlanner.isRotating;
        store.dispatch(rotatingSet(!isRotating));

        this.removeWallJumpGhost(scene);
        return 'jumped';
    }

    /**
     * Finds the parent object with an ID in the given object's hierarchy.
     * @param {THREE.Object3D<THREE.Object3DEventMap>} object - The object to find the parent of.
     * @return {THREE.Object3D | null} - The parent object with an ID, or null if none is found.
     */
    private findParentWithId = (
        object: THREE.Object3D<THREE.Object3DEventMap>
    ): THREE.Object3D | null => {
        let current = object;
        let fallbackWithId: THREE.Object3D | null = null;

        // Prefer the selectable root (placeholder / cabinet group). GLB children
        // often carry their own `userData.id`, which would otherwise short-circuit
        // and break click/drag selection for placeholders.
        while (current) {
            if (current.userData?.isPlaceholder && current.userData?.id) {
                return current;
            }
            if (
                current.userData?.id &&
                (current.userData?.values || current.userData?.isFloorProduct)
            ) {
                return current;
            }
            if (current.userData?.id && !fallbackWithId) {
                fallbackWithId = current;
            }
            if (!current.parent) break;
            current = current.parent;
        }
        return fallbackWithId;
    };

    /**
     * Checks if the cabinet is selected.
     * Prefers the in-memory selection (click / hash deep-link), then URL
     * search params. Location hash is only used before the user has an
     * explicit in-memory selection, so deep-link targets don't stay
     * highlighted after selecting another cabinet.
     * @param {CabinetMesh} selectedCabinet - The cabinet group to check.
     * @return {boolean} - True if the cabinet is selected, false otherwise.
     */
    private checkCabinetIfSelected(selectedCabinet: CabinetMesh) {
        if (this.selectedCabinet?.uuid === selectedCabinet.uuid) {
            return true;
        }

        if (selectedCabinet?.userData?.isPlaceholder) {
            const cabinetId = this.searchParams.get('selected');
            const selectedIndex = this.searchParams.get('index');
            let index: string | undefined;
            if (isNaN(Number(selectedCabinet.userData.id))) {
                [, index] = String(selectedCabinet.userData.id).split('-');
            }
            return (
                cabinetId == String(selectedCabinet.userData.id) &&
                (index == undefined || index == selectedIndex)
            );
        }

        let index: string | undefined;
        const rawMeshId = String(selectedCabinet.userData.id ?? '');
        if (rawMeshId && Number.isNaN(Number(rawMeshId))) {
            const lastDash = rawMeshId.lastIndexOf('-');
            if (lastDash !== -1) {
                index = rawMeshId.slice(lastDash + 1);
            }
        }
        // Re-sync from the live URL rather than trusting the cached copy.
        this.searchParams = new URLSearchParams(window.location.search);

        const cabinetId = this.searchParams.get('selected');
        const selectedIndex = this.searchParams.get('index');

        if (
            cabinetId != null &&
            cabinetId == selectedCabinet.userData.values?.id &&
            (index == undefined || index == selectedIndex)
        ) {
            return true;
        }

        // Hash deep-links only apply until the user (or highlightCabinet)
        // establishes an in-memory selection, or explicitly deselects.
        if (this.selectedCabinet || this.isHashSelectionDismissed) {
            return false;
        }

        const hashId = window.location.hash.replace('#', '');
        if (!hashId) {
            return false;
        }

        return (
            String(selectedCabinet.userData.values?.id) === hashId ||
            String(selectedCabinet.userData.values?.job_cabinet_id) ===
                hashId ||
            rawMeshId === hashId ||
            rawMeshId.startsWith(`${hashId}-`)
        );
    }

    /**
     * Clears the visual selection outline on a cabinet.
     * @param {CabinetMesh | null | undefined} cabinet - Cabinet to clear.
     */
    private clearCabinetHighlight(cabinet: CabinetMesh | null | undefined) {
        if (!cabinet) {
            return;
        }

        this.triggerEvents(cabinet, 'resetSegmentColor', false);
    }

    /**
     * Sets the intersections for the product events.
     * @param {THREE.Intersection<THREE.Object3D<THREE.Object3DEventMap>>[]} intersects - The intersections to be set.
     */
    private setIntersections(
        intersects: THREE.Intersection<THREE.Object3D<THREE.Object3DEventMap>>[]
    ) {
        intersects.forEach((intersect) => {
            const intersectedGroup = this.findParentWithId(
                intersect.object
            ) as CabinetMesh;

            const rawId = intersectedGroup?.userData?.id;
            if (rawId === undefined || rawId === null || rawId === '') {
                return;
            }

            // Accept numeric ids, quantity duplicates (`123-0`), and hashed preview ids.
            this.intersections.push({
                distance: intersect.distance,
                id: rawId,
                group: intersectedGroup,
            });
        });
    }

    /**
     * Checks the intersections for the product events.
     * @param {Function} successHandler - The function to call if the cabinet is selected.
     */
    private checkIntersections(
        successHandler?: (selectedCabinet: CabinetMesh) => void
    ) {
        const sortedByDistance = this.intersections?.sort(
            (a, b) => a.distance - b.distance
        );
        const closestIntersect = sortedByDistance?.[0];

        if (closestIntersect) {
            this.selectedCabinet = closestIntersect.group;
            this.intersections = [];
            successHandler?.(closestIntersect.group);
        }
    }

    /**
     * Handles arrow intersections for cabinet movement.
     * @param {MouseEvent} event - The mouse event.
     * @param {THREE.Raycaster} raycaster - The raycaster instance.
     * @param {THREE.OrthographicCamera} camera - The camera instance.
     * @param {THREE.Scene} scene - The scene instance.
     * @param {CabinetMesh} selectedCabinet - The cabinet group.
     * @param {string} index - The cabinet index.
     * @return {boolean} - True if arrow intersection was handled, false otherwise.
     */
    private handleArrowIntersections(
        event: MouseEvent,
        raycaster: THREE.Raycaster,
        camera: THREE.OrthographicCamera,
        scene: THREE.Scene,
        selectedCabinet: CabinetMesh,
        index: string
    ): boolean {
        // First check for arrow intersections - only consider visible arrow groups
        const arrowGroups = scene.children.filter(
            (child) =>
                child.name === 'cabinetMovementArrows' &&
                child.visible === true &&
                child.userData.id === selectedCabinet.userData.id
        );

        const arrowIntersects = raycaster.intersectObjects(arrowGroups, true);

        if (arrowIntersects.length > 0) {
            const arrowGroup = arrowIntersects[0].object.parent;
            if (
                arrowGroup &&
                typeof arrowGroup.userData.handleClick === 'function' &&
                arrowGroup.visible === true
            ) {
                // Only select the cabinet associated with this arrow
                if (arrowGroup.userData.id === selectedCabinet.userData.id) {
                    // Don't reset movement mode when clicking on arrows - let the arrow handleClick process first
                    selectCabinet(selectedCabinet, camera, this.renderer);
                    // Set this cabinet as the active group
                    if (this.setSelectedCabinetGroup) {
                        this.setSelectedCabinetGroup(selectedCabinet);
                    }

                    // Update search params to reflect selection
                    const values = selectedCabinet.userData.values;
                    if (values) {
                        this.searchParams.set(
                            'selected',
                            values.job_cabinet_id?.toString() || ''
                        );

                        if (index !== undefined) {
                            this.searchParams.set('index', index);
                        }

                        this.updateSearchParams(this.searchParams);
                    }

                    (
                        arrowGroup.userData as {
                            handleClick: (
                                event: MouseEvent,
                                raycaster: THREE.Raycaster,
                                camera: THREE.Camera
                            ) => void;
                        }
                    ).handleClick(event, raycaster, camera);
                }
                return true; // Arrow intersection was handled
            }
        }

        return false; // No arrow intersection was handled
    }

    /**
     * Handles scene intersections for cabinet selection/deselection.
     * @param {THREE.Raycaster} raycaster - The raycaster instance.
     * @param {THREE.OrthographicCamera} camera - The camera instance.
     * @param {THREE.Scene} scene - The scene instance.
     * @param {CabinetMesh} selectedCabinet - The cabinet group.
     */
    private handleSceneIntersections(
        raycaster: THREE.Raycaster,
        camera: THREE.OrthographicCamera,
        scene: THREE.Scene,
        selectedCabinet: CabinetMesh
    ): void {
        const sceneIntersect = raycaster.intersectObjects(scene.children, true);

        const sortedByDistance = sceneIntersect.sort(
            (a, b) => a.distance - b.distance
        );

        // Only deselect if we clicked on floor/wall and not on an arrow
        if (sceneIntersect.length <= 0) {
            // Check if edit sidebar is open (preventReset parameter indicates edit mode)
            const isEditSidebarOpen = this.isEditSidebarOpen();

            if (!isEditSidebarOpen) {
                store.dispatch(setIsRoomPlannerEditing(false));
                this.clearSelectionFromUrl();
                if (this.setSelectedCabinetGroup) {
                    this.setSelectedCabinetGroup(null);
                }
            }
            // When edit sidebar is open, don't change any state to preserve highlight and sidebar
        }
        // Only deselect if we clicked on floor/wall and not on an arrow
        const isWallOrFloor = sortedByDistance
            .map((data) => data.object.name.toLowerCase())
            .map(
                (name) =>
                    name.indexOf('wall') !== -1 || name.indexOf('floor') !== -1
            )
            .some(Boolean);

        if (isWallOrFloor) {
            // Check if movement arrows are visible for this cabinet
            const movementArrows = scene.children.find(
                (child) =>
                    child.name === 'cabinetMovementArrows' &&
                    child.userData?.id === selectedCabinet.userData.id
            );

            // Hide distance wall measurements when deselecting, but only if movement mode is not active
            if (!selectedCabinet.userData.isMovementMode) {
                scene.children.forEach((child) => {
                    if (child.name === 'distanceWallMeasurements') {
                        child.visible = false;
                        child.traverse((arrow) => {
                            arrow.visible = false;
                        });
                    }
                });
            }

            if (movementArrows?.visible) {
                // If movement arrows are visible, keep the cabinet active
                // Reset movement mode for all cabinets before keeping this one active, excluding current cabinet
                this.resetMovementModeForAllCabinets(selectedCabinet);

                if (this.setSelectedCabinetGroup) {
                    this.setSelectedCabinetGroup(selectedCabinet);
                }
                selectCabinet(selectedCabinet, camera, this.renderer);
                store.dispatch(
                    setSelectedProductValue(selectedCabinet.userData.values)
                );
            } else {
                // Check if edit sidebar is open (preventReset parameter indicates edit mode)

                const isEditSidebarOpen = this.isEditSidebarOpen();

                if (!isEditSidebarOpen) {
                    // Otherwise, deselect as usual
                    store.dispatch(setIsRoomPlannerEditing(false));
                    this.deselectCabinet();
                    if (this.setSelectedCabinetGroup) {
                        this.setSelectedCabinetGroup(null);
                    }
                }
            }
        }
    }

    /**
     * Revalidates overlaps for all products in the room planner.
     * Checks each product against all other products to detect collisions/overlaps
     * and triggers appropriate overlap events when conflicts are found
     * @public
     * @memberof ProductEvents
     * @return {void}
     */
    public revalidateOverlaps() {
        setTimeout(() => {
            if (!this.products?.length || this.isGeneratingReport) return;

            const meshes = this.getLayoutMeshes();
            meshes.forEach((cabinet) => {
                overlapValidator.cabinet(cabinet, meshes, (hasOverlap) => {
                    const isSelected = this.checkCabinetIfSelected(cabinet);
                    if (hasOverlap) {
                        this.triggerEvents(cabinet, 'onOverlap', true);
                    } else if (isSelected) {
                        // Keep selection highlight when still selected
                        this.triggerEvents(cabinet, 'onCabinetSelect', true);
                    } else {
                        this.triggerEvents(cabinet, 'resetSegmentColor', false);
                    }
                });
            });
        }, 10);
    }

    /**
     * Gets all visible clickable text sprites from the scene
     * @param {THREE.Scene} scene - The scene containing the sprites
     * @private
     * @return {THREE.Sprite[]} - Array of visible clickable sprites
     */
    private getVisibleClickableSprites(
        scene: THREE.Scene
    ): THREE.Sprite<THREE.Object3DEventMap>[] {
        return scene.children
            .filter((child) => child.visible === true)
            .flatMap((child) => {
                const sprites: THREE.Sprite<THREE.Object3DEventMap>[] = [];
                child.traverse((obj) => {
                    if (
                        obj instanceof THREE.Sprite &&
                        obj.userData?.isTextSprite &&
                        obj.userData?.clickable
                    ) {
                        sprites.push(
                            obj as THREE.Sprite<THREE.Object3DEventMap>
                        );
                    }
                });
                return sprites;
            });
    }

    /**
     * Handles hover effects for text sprites (like distance measurements)
     * This allows sprites to be hovered even when occluded by other objects
     * @param {THREE.Scene} scene - The scene containing the sprites
     * @private
     * @return {void}
     */
    private handleSpriteHover(scene: THREE.Scene): void {
        if (
            this.selectedCabinet &&
            this.shouldPrioritizeMovementArrow(
                this.raycaster,
                scene,
                this.selectedCabinet
            )
        ) {
            if (this.hoveredSprite?.userData?.isHovered) {
                if (
                    typeof this.hoveredSprite.userData.onHoverEnd === 'function'
                ) {
                    (
                        this.hoveredSprite.userData as {
                            onHoverEnd: () => void;
                        }
                    ).onHoverEnd();
                }
                this.hoveredSprite.userData.isHovered = false;
            }
            this.hoveredSprite = null;
            return;
        }

        // Get all visible clickable text sprites
        const allSprites = this.getVisibleClickableSprites(scene);

        // Check for text sprite intersections
        if (allSprites.length > 0) {
            const spriteIntersects = this.raycaster.intersectObjects(
                allSprites,
                false
            );

            if (spriteIntersects.length > 0) {
                const sprite = spriteIntersects[0].object as THREE.Sprite;
                this.hoveredSprite = sprite; // Track the currently hovered sprite

                if (sprite.userData?.isTextSprite && sprite.userData?.onHover) {
                    // Apply hover effect if not already hovered
                    if (!sprite.userData.isHovered) {
                        if (typeof sprite.userData.onHover === 'function') {
                            (
                                sprite.userData as {
                                    onHover: () => void;
                                }
                            ).onHover();
                        }
                        sprite.userData.isHovered = true;
                    }
                }

                // Remove hover from other sprites
                allSprites.forEach((otherSprite) => {
                    if (
                        otherSprite !== sprite &&
                        otherSprite.userData?.isHovered
                    ) {
                        if (
                            typeof otherSprite.userData.onHoverEnd ===
                            'function'
                        ) {
                            (
                                otherSprite.userData as {
                                    onHoverEnd: () => void;
                                }
                            ).onHoverEnd();
                        }
                        otherSprite.userData.isHovered = false;
                    }
                });
            } else {
                // Remove hover from all sprites if no intersection
                this.hoveredSprite = null;
                allSprites.forEach((sprite) => {
                    if (sprite.userData?.isHovered) {
                        if (typeof sprite.userData.onHoverEnd === 'function') {
                            (
                                sprite.userData as {
                                    onHoverEnd: () => void;
                                }
                            ).onHoverEnd();
                        }
                        sprite.userData.isHovered = false;
                    }
                });
            }
        } else {
            this.hoveredSprite = null;
        }
    }

    /**
     * Sets the side button refs and callbacks for handling side button clicks
     * @param {SideButtonRefs} refs - The button refs
     * @param {SideButtonCallbacks} callbacks - The button click callbacks
     */
    public setSideButtonHandlers(
        refs: SideButtonRefs,
        callbacks: SideButtonCallbacks
    ): void {
        this.sideButtonRefs = refs;
        this.sideButtonCallbacks = callbacks;
    }

    /**
     * Checks if a sprite is currently hovered and handles click event
     * This allows clicking on sprites even when they're occluded by other objects
     * @param {THREE.Scene} scene - The scene containing the sprites
     * @private
     * @return {boolean} - True if a hovered sprite was clicked, false otherwise
     */
    private checkAndClickHoveredSprite(scene: THREE.Scene): boolean {
        if (
            this.selectedCabinet &&
            this.shouldPrioritizeMovementArrow(
                this.raycaster,
                scene,
                this.selectedCabinet
            )
        ) {
            return false;
        }

        // If we have a hovered sprite, check if it's still valid and clickable
        if (this.hoveredSprite && this.hoveredSprite.userData?.onClick) {
            // Verify the sprite is still in the scene and visible
            const allSprites = this.getVisibleClickableSprites(scene);
            const spriteStillValid = allSprites.includes(this.hoveredSprite);

            if (spriteStillValid) {
                this.clickedSprite = this.hoveredSprite;
                return true;
            }
        }
        return false;
    }

    /**
     * Placeholder-only: bind click/drag if this canvas has no listeners yet.
     * Cabinet render uses `attachEvents` directly — do not call this from there.
     * @param {object} args Scene, camera, controls, room type
     * @return {this}
     */
    public ensurePlaceholderCanvasEvents(args: {
        scene: THREE.Scene;
        camera: THREE.OrthographicCamera;
        controls: OrbitControls;
        roomType: RoomType;
    }) {
        const el = this.renderer?.domElement ?? null;
        if (
            this.eventHandlers.size > 0 &&
            this.boundDomElement &&
            el &&
            this.boundDomElement === el
        ) {
            return this;
        }
        this.attachEvents(args);
        return this;
    }

    /**
     * Attaches events to the specified cabinet group.
     * @param {THREE.Scene} scene - The scene to attach events to.
     * @param {THREE.OrthographicCamera} camera - The camera to use for raycasting.
     * @param {OrbitControls} controls - The controls to use for camera manipulation.
     * @param {RoomType} roomType - The type of room (e.g., LSHAPED).
     */
    public attachEvents({
        scene,
        camera,
        controls,
        roomType,
    }: {
        scene: THREE.Scene;
        camera: THREE.OrthographicCamera;
        controls: OrbitControls;
        roomType: RoomType;
    }) {
        // One set of canvas listeners — replace rather than stacking.
        this.clearEvents();

        const raycaster = new THREE.Raycaster();
        const mouse = new THREE.Vector2();
        let lockedFloorYPoint: number;
        let isDragging = false;
        const previousPosition = new THREE.Vector3();
        const dragOffset = new THREE.Vector3();

        const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);

        const wall = this.getCachedWallMesh(scene);
        const onMouseDown = (event: MouseEvent) => {
            if (event.button !== 0) return;

            // A press starts a new click cycle even with nothing selected;
            // otherwise a flag left over from the last drag (e.g. before
            // Escape deselected) makes handleLeftClick ignore every click.
            hasMouseMoved = false;

            if (!this.selectedCabinet) return;

            if (this.selectedCabinet.userData.isSnapMode) {
                this.selectedCabinet.userData.wasSnapped = true;
            }
            const showCabinets = store.getState().roomPlanner.showCabinets;
            if (!showCabinets) return;

            // Reset all panning flags
            this.resetPanningState('all');

            // Track side button mouse down for drag detection
            if (this.sideButtonRefs) {
                this.sideButtonMouseDown = {x: event.clientX, y: event.clientY};
                this.sideButtonMouseMoved = false;
            }

            // Reset sprite click flag at the start of each click cycle
            this.spriteWasClicked = false;
            // Reset side button click flag at the start of each click cycle
            this.sideButtonWasClicked = false;

            // Prevent interaction if quick add is still pending
            if (this.selectedCabinet.userData.isQuickAddPending) {
                return;
            }

            const dimension = this.container.getBoundingClientRect();
            mouse.x =
                ((event.clientX - dimension.left) / dimension.width) * 2 - 1;
            mouse.y =
                (-(event.clientY - dimension.top) / dimension.height) * 2 + 1;

            raycaster.setFromCamera(mouse, camera);

            // Check if we're clicking on a hovered sprite (even if occluded)
            // This allows clicking on distance measurements even when objects are in front
            if (this.checkAndClickHoveredSprite(scene)) {
                return; // Exit early if we clicked a hovered sprite
            }

            // EXPLICITLY check side buttons FIRST before any other raycasting
            // Check buttons if they exist and button group is visible
            // Only skip if button is explicitly set to false
            if (
                this.sideButtonRefs &&
                this.selectedCabinet &&
                this.sideButtonRefs.buttonGroup.current?.visible
            ) {
                const buttonsToCheck: THREE.Sprite[] = [];

                // Add buttons if they exist and are not explicitly hidden
                if (
                    this.sideButtonRefs.leftButton.current &&
                    this.sideButtonRefs.leftButton.current.visible !== false
                ) {
                    buttonsToCheck.push(this.sideButtonRefs.leftButton.current);
                }
                if (
                    this.sideButtonRefs.rightButton.current &&
                    this.sideButtonRefs.rightButton.current.visible !== false
                ) {
                    buttonsToCheck.push(
                        this.sideButtonRefs.rightButton.current
                    );
                }

                if (buttonsToCheck.length > 0) {
                    // Update matrices for sprites before raycasting
                    buttonsToCheck.forEach((button) => {
                        button.updateMatrixWorld(true);
                    });
                    const buttonIntersects = raycaster.intersectObjects(
                        buttonsToCheck,
                        false
                    );

                    if (buttonIntersects.length > 0) {
                        // Button was clicked - don't start dragging
                        // The actual click handling will be done in onMouseUp
                        return;
                    }
                }
            }

            // Check for text sprite intersections on mouse down with occlusion checking
            const allIntersects = raycaster.intersectObjects(
                scene.children.filter((obj) => obj.visible === true),
                true
            );

            // Find the closest intersection overall
            const closestIntersect = allIntersects[0];

            // Check if the closest intersection is a text sprite
            if (
                closestIntersect &&
                closestIntersect.object.userData?.isTextSprite
            ) {
                this.clickedSprite = closestIntersect.object as THREE.Sprite;
                return; // Exit early if we clicked a sprite that's not occluded
            }

            // Also check if buttons are in the scene raycasting (backup check)
            // This ensures we catch button clicks even if they're in the scene children
            if (
                isSideButtonClick(
                    allIntersects,
                    closestIntersect,
                    this.sideButtonRefs
                ) ||
                isAnySideButtonIntersected(allIntersects, this.sideButtonRefs)
            ) {
                // Clicked on a side button - don't start dragging
                return;
            }

            const intersects = raycaster.intersectObject(this.selectedCabinet);

            if (
                intersects.length > 0 &&
                this.findParentWithId(intersects[0].object)?.userData.id ==
                    this.selectedCabinet.userData.id
            ) {
                if (this.isPreview) {
                    this.panningState.left.active = true;
                    controls.enabled = true;
                    return;
                }
                // Set local isDragging for drag tracking,
                isDragging = true;
                this.panningState.left.active = false; // Not panning, we're dragging the cabinet

                this.checkCabinetIfSelected(this.selectedCabinet) &&
                    (controls.enabled = false);

                const intersectPoint = intersects[0].point;

                lockedFloorYPoint = intersectPoint.y;

                plane.setFromNormalAndCoplanarPoint(
                    new THREE.Vector3(0, 1, 0), // normal ↑
                    intersectPoint // at the Y you clicked
                );

                dragOffset
                    .copy(intersectPoint)
                    .sub(this.selectedCabinet.position);
            } else {
                // Clicked on empty space - enable panning mode
                this.panningState.left.active = true;
                controls.enabled = true;
            }
        };

        const onRightMouseDown = (event: MouseEvent) => {
            // Right-click (button 2) - enable right-click panning mode
            if (event.button === 2) {
                this.resetPanningState('right');
                this.panningState.right.active = true;
                controls.enabled = true;
            }
        };

        const onMouseMove = throttle((event: MouseEvent) => {
            if (!this.selectedCabinet) return;

            // If panning (either left or right click), keep controls enabled and track movement
            if (
                this.panningState.right.active ||
                this.panningState.left.active
            ) {
                controls.enabled = true;
                if (this.panningState.right.active) {
                    this.panningState.right.mouseMoved = true;
                }
                if (this.panningState.left.active) {
                    this.panningState.left.mouseMoved = true;
                }
                return;
            }

            // Track side button mouse movement for drag detection
            if (this.sideButtonRefs && this.sideButtonMouseDown) {
                this.sideButtonMouseMoved = trackSideButtonMouseMove(
                    event,
                    this.sideButtonMouseDown
                );
            }

            // Prevent overlapping mouse move processing
            if (this.isProcessingMouseMove) {
                this.pendingMouseMove = event;
                return;
            }

            this.isProcessingMouseMove = true;

            const {height} = getGroupWidth(this.selectedCabinet);

            try {
                // Prevent movement if we're within 75ms of a snap
                if (Date.now() - this.lastSnapTimestamp < 75) {
                    return;
                }

                // Use optimized mouse position update
                const mouseMoved = this.updateMousePosition(event, camera);

                // Only travel since the press makes it a drag. mouseMoved
                // compares with the last processed hover position, so a
                // throttled or queued move landing just after mousedown
                // would turn a still click into a drag and drop it.
                if (
                    !hasMouseMoved &&
                    Math.hypot(
                        event.clientX - pressStart.x,
                        event.clientY - pressStart.y
                    ) > CLICK_MOVE_THRESHOLD
                ) {
                    hasMouseMoved = true;
                }

                if (!mouseMoved && !isDragging) {
                    return; // Skip processing if mouse didn't move significantly and not dragging
                }

                // Handle text sprite hover effects (for distance measurements)
                this.handleSpriteHover(scene);

                // Handle reposition arrow hover: only the front-most arrow under the cursor
                const activeGroupId = this.selectedCabinet.userData.id;
                const arrowGroups = this.getCachedArrowGroups(
                    scene,
                    activeGroupId
                );

                const hoveredArrow =
                    this.selectedCabinet.userData.isMovementMode &&
                    arrowGroups.length > 0
                        ? this.getClosestMovementArrowMesh(
                              this.raycaster,
                              scene,
                              this.selectedCabinet
                          )
                        : null;

                arrowGroups.forEach((arrowGroup) => {
                    arrowGroup.traverse((child) => {
                        if (!child.userData?.isArrow) {
                            return;
                        }

                        const shouldHover = child === hoveredArrow;

                        if (shouldHover && !child.userData.isHovered) {
                            if (typeof child.userData.onHover === 'function') {
                                (
                                    child.userData as {
                                        onHover: () => void;
                                    }
                                ).onHover();
                            }
                        } else if (!shouldHover && child.userData.isHovered) {
                            if (
                                typeof child.userData.onHoverEnd === 'function'
                            ) {
                                (
                                    child.userData as {
                                        onHoverEnd: () => void;
                                    }
                                ).onHoverEnd();
                            }
                        }
                    });
                });

                // Only continue with dragging logic if actually dragging.
                // Hold still until past the click threshold, so a click
                // never nudges the cabinet without the drag being saved.
                if (!isDragging || !hasMouseMoved) return;

                const isSelected = this.checkCabinetIfSelected(
                    this.selectedCabinet
                );
                if (!isSelected) return;

                // Only set isDragging state when mouse has actually moved (indicating a drag, not just a click)
                if (!this.isDragging && hasMouseMoved) {
                    this.isDragging = true;
                    store.dispatch(setIsDragging(true));
                    if (
                        !this.selectedCabinet.userData?.isPlaceholder &&
                        this.selectedCabinet.userData.isDoorOrDrawerOpen
                    ) {
                        // Keep local door state in sync with forced-close on drag start.
                        // Dashed wall guides read this flag to choose closed/open footprint logic.
                        this.selectedCabinet.userData.isDoorOrDrawerOpen = false;
                        const product = this.selectedCabinet.userData.values;
                        this.triggerEvents(this.selectedCabinet, 'toggleDoor', {
                            doorGap: product?.cabinet_door_gap,
                            isDoorOrDrawerOpen: false,
                        });
                    }
                }
                if (controls.enabled) controls.enabled = false;

                const intersectionPoint = new THREE.Vector3();
                this.raycaster.ray.intersectPlane(plane, intersectionPoint);
                const dragPosition = intersectionPoint.sub(dragOffset);

                this.setDragPosition(dragPosition);

                let cabinetToMove = this.ghostPanelCabinet
                    ? this.ghostPanelCabinet
                    : this.selectedCabinet;

                // Snap state lives on the product in memory; each drag frame
                // applies what handleSnapping decided (applySnapState below).
                const isSnapMode = cabinetToMove.userData.isSnapMode;
                const bounds = cabinetToMove.userData.bounds;

                const isUpperProduct =
                    this.selectedCabinet?.userData?.isUpperProduct;

                // Horizontal-only drag for upper cabinets: freeze the depth
                // axis at its current value so the mouse can only slide the
                // cabinet along the wall it is mounted on.
                const upperLateralDragLock =
                    isUpperCabinetWallLocked(cabinetToMove);
                const attachedWall = cabinetToMove.userData.currentWall;
                const attachedWallMesh =
                    attachedWall && attachedWall.isMesh
                        ? attachedWall
                        : undefined;
                const attachedWallBox = attachedWallMesh
                    ? getWallSurfaceBox(attachedWallMesh)
                    : null;
                const fallbackWallDirection = getDirection(
                    cabinetToMove.rotation.z
                );
                const upperDepthIsX = attachedWallBox
                    ? getWallNormalAxis(attachedWallBox) === 'x'
                    : fallbackWallDirection === 'left' ||
                      fallbackWallDirection === 'right';
                const upperDepthValue = upperDepthIsX
                    ? cabinetToMove.position.x
                    : cabinetToMove.position.z;
                cabinetToMove.updateMatrixWorld(true);
                const cabinetDragBox = new THREE.Box3().setFromObject(
                    cabinetToMove
                );
                const lateralPadMin = upperDepthIsX
                    ? cabinetToMove.position.z - cabinetDragBox.min.z
                    : cabinetToMove.position.x - cabinetDragBox.min.x;
                const lateralPadMax = upperDepthIsX
                    ? cabinetDragBox.max.z - cabinetToMove.position.z
                    : cabinetDragBox.max.x - cabinetToMove.position.x;
                const freezeUpperDepth = (position: THREE.Vector3) => {
                    if (upperDepthIsX) {
                        position.x = upperDepthValue;
                    } else {
                        position.z = upperDepthValue;
                    }
                };
                const clampUpperLateralToWall = (position: THREE.Vector3) => {
                    if (!attachedWallBox || attachedWallBox.isEmpty()) {
                        return;
                    }
                    if (upperDepthIsX) {
                        const min = attachedWallBox.min.z + lateralPadMin;
                        const max = attachedWallBox.max.z - lateralPadMax;
                        if (Number.isFinite(min) && Number.isFinite(max)) {
                            position.z = THREE.MathUtils.clamp(
                                position.z,
                                Math.min(min, max),
                                Math.max(min, max)
                            );
                        }
                        return;
                    }
                    const min = attachedWallBox.min.x + lateralPadMin;
                    const max = attachedWallBox.max.x - lateralPadMax;
                    if (Number.isFinite(min) && Number.isFinite(max)) {
                        position.x = THREE.MathUtils.clamp(
                            position.x,
                            Math.min(min, max),
                            Math.max(min, max)
                        );
                    }
                };
                const constrainUpperWallDrag = (position: THREE.Vector3) => {
                    if (!upperLateralDragLock) {
                        return;
                    }
                    freezeUpperDepth(position);
                    clampUpperLateralToWall(position);
                };
                // Raw mouse target kept so the cabinet can follow the mouse
                // onto a new wall when auto-rotation fires mid-drag.
                const rawDragPosition = dragPosition.clone();

                constrainUpperWallDrag(dragPosition);

                const targetPos = dragPosition.clone();
                // Pre-clamp overlaps are only for snap candidate detection.
                // Visual red / blocking must use the post-clamp position below
                // so L-notch (rightWidth2Wall) mouse targets don't false-positive.
                const overlappingCabinetsForSnap = checkCabinetOverlap(
                    cabinetToMove,
                    targetPos,
                    this.products
                );

                const snapOutcome = handleSnapping(
                    cabinetToMove,
                    targetPos,
                    this.products,
                    {
                        camera,
                        overlappingCabinets: overlappingCabinetsForSnap,
                        wallGroup: wall,
                    }
                );

                applySnapState(cabinetToMove, snapOutcome?.snapState);

                // A copy of the target when nothing snapped: the drag below
                // moves snapPosition about without touching targetPos.
                let snapPosition = targetPos.clone();
                let snapped = false;
                if (isSnapCandidate(snapOutcome)) {
                    snapPosition = snapOutcome.position;
                    snapped = true;
                }

                const isGhostPanelPossible =
                    this.selectedCabinet.userData.isVerminProduct ||
                    this.selectedCabinet.userData.isFillerProduct ||
                    this.selectedCabinet.userData.isManualPanel ||
                    this.selectedCabinet.userData.isPanelProduct;

                let detachedFromSnapMode = false;
                if (isSnapMode && !upperLateralDragLock) {
                    const direction = getDirection(cabinetToMove.rotation.z);
                    if (direction != 'left' && direction != 'right') {
                        if (dragPosition.y < bounds[3] - 25) {
                            detachedFromSnapMode = true;
                            if (
                                this.selectedCabinet.userData.wasSnapped &&
                                isGhostPanelPossible
                            ) {
                                this.renderGhostPanelCabinet(
                                    isUpperProduct,
                                    this.selectedCabinet,
                                    scene,
                                    targetPos,
                                    this.selectedCabinet.position.y
                                );
                                cabinetToMove = this.ghostPanelCabinet;
                            }
                            if (cabinetToMove.userData.isGhostPanel) {
                                if (
                                    this.selectedCabinet.userData.snappedSide ==
                                    'right'
                                ) {
                                    dragPosition.x += 50;
                                    cabinetToMove.position.x += 50;
                                } else if (
                                    this.selectedCabinet.userData.snappedSide ==
                                    'left'
                                ) {
                                    dragPosition.x -= 50;
                                    cabinetToMove.position.x -= 50;
                                }
                            }

                            clearSnapState(cabinetToMove);
                        } else {
                            dragPosition.x = cabinetToMove.position.x;
                        }
                    } else {
                        if (dragPosition.y < bounds[3] - 25) {
                            detachedFromSnapMode = true;
                            if (
                                this.selectedCabinet.userData.wasSnapped &&
                                isGhostPanelPossible
                            ) {
                                this.renderGhostPanelCabinet(
                                    isUpperProduct,
                                    this.selectedCabinet,
                                    scene,
                                    targetPos,
                                    this.selectedCabinet.position.y
                                );
                                cabinetToMove = this.ghostPanelCabinet;
                            }
                            if (cabinetToMove.userData.isGhostPanel) {
                                if (
                                    this.selectedCabinet.userData.snappedSide ==
                                    'normal'
                                ) {
                                    dragPosition.z += 50;

                                    cabinetToMove.position.z += 50;
                                } else if (
                                    this.selectedCabinet.userData.snappedSide ==
                                    'back'
                                ) {
                                    dragPosition.z -= 50;
                                    cabinetToMove.position.z -= 50;
                                }
                            }

                            clearSnapState(cabinetToMove);
                        } else {
                            dragPosition.z = cabinetToMove.position.z;
                        }
                    }
                }

                // handleSnapping ran while still attached, so this is stale.
                if (detachedFromSnapMode) {
                    snapped = false;
                    snapPosition.copy(dragPosition);
                }

                previousPosition.copy(cabinetToMove.position);
                // Keep the whole pose: a rejected snap must not leave the old
                // position paired with productClamping's new rotation.
                const previousRotationZ = cabinetToMove.rotation.z;
                const previousCurrentWall = cabinetToMove.userData.currentWall;
                const previousCurrentDirection =
                    cabinetToMove.userData.currentDirection;
                const restorePreviousPose = () => {
                    cabinetToMove.position.copy(previousPosition);
                    cabinetToMove.rotation.z = previousRotationZ;
                    cabinetToMove.userData.currentWall = previousCurrentWall;
                    cabinetToMove.userData.currentDirection =
                        previousCurrentDirection;
                    cabinetToMove.updateMatrixWorld(true);
                };

                if (isSnapMode && !upperLateralDragLock) {
                    const direction = getDirection(cabinetToMove.rotation.z);
                    if (direction != 'left' && direction != 'right') {
                        plane.setFromNormalAndCoplanarPoint(
                            new THREE.Vector3(1, 0, 0),
                            cabinetToMove.position
                        );
                    } else {
                        plane.setFromNormalAndCoplanarPoint(
                            new THREE.Vector3(0, 0, 1),
                            cabinetToMove.position
                        );
                    }
                } else {
                    if (cabinetToMove.userData.isGhostPanel) {
                        const box = new THREE.Box3().setFromObject(
                            cabinetToMove
                        );
                        const size = new THREE.Vector3();
                        box.getSize(size);

                        // Half the object's height
                        const halfHeight = size.y / 2;
                        // Standard floor dragging
                        plane.setFromNormalAndCoplanarPoint(
                            new THREE.Vector3(0, 1, 0),
                            new THREE.Vector3(
                                cabinetToMove.position.x,
                                cabinetToMove.position.y + halfHeight,
                                cabinetToMove.position.z
                            )
                        );
                    } else {
                        // Standard floor dragging
                        plane.setFromNormalAndCoplanarPoint(
                            new THREE.Vector3(0, 1, 0),
                            new THREE.Vector3(0, lockedFloorYPoint, 0)
                        );
                    }
                }

                // Upper-cabinet drag: the cabinet itself never auto-rotates
                // in place (preventAutoRotation); instead, hovering another
                // wall shows a ghost preview pinned to that wall and the
                // cabinet jumps there once the spot has space. The depth-axis
                // freeze keeps the cabinet from being pulled off its current
                // wall while preserving any keyboard/arrow offset.
                if (upperLateralDragLock && wall) {
                    const jumpState = this.handleUpperWallJump(
                        cabinetToMove,
                        wall,
                        camera,
                        scene,
                        rawDragPosition,
                        roomType
                    );

                    // 'jumped': the cabinet was placed flush on the new wall
                    // this frame. 'preview': the ghost is showing on the
                    // other wall, keep the original cabinet frozen in place
                    // instead of letting it keep sliding on its own wall.
                    if (jumpState !== 'none') {
                        return;
                    }
                }

                const newBasePosition = productClamping(
                    cabinetToMove,
                    wall,
                    camera,
                    this.mouse,
                    dragPosition,
                    roomType,
                    false,
                    upperLateralDragLock
                        ? true
                        : cabinetToMove.userData.isSnapMode ||
                              !!cabinetToMove.userData?.isPlaceholder,
                    this.products
                );

                // Guard against NaN clamp results (placeholders especially)
                if (
                    !newBasePosition?.clamped ||
                    !isFinite(newBasePosition.clamped.x) ||
                    !isFinite(newBasePosition.clamped.y) ||
                    !isFinite(newBasePosition.clamped.z)
                ) {
                    return;
                }

                constrainUpperWallDrag(newBasePosition.clamped);

                // Always keep current-frame clamp bounds (not only after snap)
                // so debounced overlap checks never use stale L-notch limits.
                cabinetToMove.userData.newBasePosition = newBasePosition;
                if (this.selectedCabinet) {
                    this.selectedCabinet.userData.newBasePosition =
                        newBasePosition;
                }

                const candidatePos = snapped
                    ? snapPosition.clone()
                    : newBasePosition.clamped.clone();
                if (snapped) {
                    candidatePos.x = THREE.MathUtils.clamp(
                        candidatePos.x,
                        newBasePosition.minX,
                        newBasePosition.maxX
                    );
                    candidatePos.z = THREE.MathUtils.clamp(
                        candidatePos.z,
                        newBasePosition.minZ,
                        newBasePosition.maxZ
                    );
                }
                constrainUpperWallDrag(candidatePos);
                constrainUpperWallDrag(snapPosition);

                // Heavy wall/notch clamp (e.g. mouse in L void): only trust the
                // clamped pose so we don't false-positive against far cabinets.
                // Otherwise use the raw target like before so cabinet-to-cabinet
                // overlap still blocks and turns red.
                const heavilyClamped =
                    !!newBasePosition.wasClamped &&
                    !!newBasePosition.clamped &&
                    targetPos.distanceTo(newBasePosition.clamped) > 25;

                // A kick-length panel sits under the carcase, among a legged
                // cabinet's adjustable legs, so the legs don't count (R-8).
                const ignoreAdjustableLegs = Boolean(
                    cabinetToMove.userData.isKickerLengthProduct
                );
                const overlappingCabinets = checkCabinetOverlap(
                    cabinetToMove,
                    heavilyClamped ? candidatePos : targetPos,
                    this.products,
                    0,
                    false,
                    ignoreAdjustableLegs
                );
                const bothFloorProducts =
                    overlappingCabinets?.some(
                        (cabinet) =>
                            cabinet.userData.isPlaceholder ||
                            cabinet.userData.isFloorProduct ||
                            cabinet.userData.noBottom
                    ) &&
                    (this.selectedCabinet?.userData.isPlaceholder ||
                        this.selectedCabinet?.userData.isFloorProduct);

                const hasOverlap =
                    !!overlappingCabinets &&
                    // Trigger overlap event when both floor products overlap
                    (bothFloorProducts ||
                        !this.selectedCabinet.userData.ignoreOverlap);

                if (!cabinetToMove.userData.isGhostPanel) {
                    this.renderGhostCabinet(
                        hasOverlap,
                        isUpperProduct,
                        this.selectedCabinet,
                        scene,
                        hasOverlap ? targetPos : candidatePos,
                        this.selectedCabinet.position.y
                    );
                }

                // Set the previous position to be used for fallback when the cabinet overlaps
                if (!hasOverlap) {
                    // Reverts fall back to this, so never store an off-floor pose.
                    const fallbackOverlaps =
                        snapped &&
                        !!checkCabinetOverlap(
                            cabinetToMove,
                            snapPosition,
                            this.products,
                            -1,
                            false,
                            ignoreAdjustableLegs
                        );
                    if (
                        !newBasePosition.wasClamped &&
                        !fallbackOverlaps &&
                        isPoseOnWalkableFloor(
                            cabinetToMove,
                            snapped ? snapPosition : targetPos
                        )
                    )
                        previousPosition.copy(
                            snapped ? snapPosition : targetPos
                        );
                    // Restore selection blue only on the dragged cabinet
                    this.triggerEvents(cabinetToMove, 'onCabinetSelect', true);
                } else {
                    if (isSnapMode == false) {
                        this.triggerEvents(cabinetToMove, 'onOverlap', true);
                        // Also paint the cabinets we're colliding with so red
                        // is visible without needing a later floor click.
                        overlappingCabinets?.forEach((otherCabinet) => {
                            this.triggerEvents(otherCabinet, 'onOverlap', true);
                        });
                    }
                }

                if (snapped) {
                    cabinetToMove.position.copy(snapPosition);
                    // Clamp X/Z to wall bounds so upper panels cannot snap outside walls
                    cabinetToMove.position.x = THREE.MathUtils.clamp(
                        cabinetToMove.position.x,
                        newBasePosition.minX,
                        newBasePosition.maxX
                    );
                    cabinetToMove.position.z = THREE.MathUtils.clamp(
                        cabinetToMove.position.z,
                        newBasePosition.minZ,
                        newBasePosition.maxZ
                    );
                    constrainUpperWallDrag(cabinetToMove.position);

                    if (bounds && isSnapMode) {
                        const boundsMaxY = bounds[1];
                        const boundsMinY = bounds[3];

                        const clampedMinY = boundsMinY;
                        const clampedMaxY = boundsMaxY - height;

                        cabinetToMove.position.y = Math.min(
                            Math.max(cabinetToMove.position.y, clampedMinY),
                            clampedMaxY
                        );
                    }
                    const hasOverlapAfterSnap = !!checkCabinetOverlap(
                        cabinetToMove,
                        cabinetToMove.position,
                        this.products,
                        -1,
                        false,
                        ignoreAdjustableLegs
                    );

                    if (hasOverlapAfterSnap) {
                        this.triggerEvents(cabinetToMove, 'onOverlap', true);
                        restorePreviousPose();
                    } else if (
                        !isPoseOnWalkableFloor(
                            cabinetToMove,
                            cabinetToMove.position
                        )
                    ) {
                        restorePreviousPose();
                    } else {
                        this.ghostCabinet?.position.copy(snapPosition);
                        this.lastSnapTimestamp = Date.now();
                        this.triggerEvents(
                            cabinetToMove,
                            'onCabinetSelect',
                            true
                        );
                    }

                    this.triggerEvents(this.selectedCabinet, 'onSnap', true);
                } else {
                    // Remove separate handling for upper product to streamline logic
                    if (hasOverlap) {
                        cabinetToMove.position.lerp(previousPosition, 0.5);
                    } else {
                        this.triggerEvents(
                            this.selectedCabinet,
                            'onSnap',
                            false
                        );

                        if (bounds) {
                            if (this.selectedCabinet.userData.isUpperProduct) {
                                if (bounds) {
                                    newBasePosition.clamped.y =
                                        bounds[1] - height;
                                }
                            } else {
                                newBasePosition.clamped.y = bounds[3];
                            }
                        }

                        if (
                            this.selectedCabinet.userData.wasSnapped &&
                            isGhostPanelPossible
                        ) {
                            this.renderGhostPanelCabinet(
                                isUpperProduct,
                                this.selectedCabinet,
                                scene,
                                targetPos,
                                this.selectedCabinet.position.y
                            );
                        } else {
                            cabinetToMove.position.copy(
                                newBasePosition.clamped
                            );
                            constrainUpperWallDrag(cabinetToMove.position);
                        }

                        if (bounds) {
                            clearSnapState(cabinetToMove);
                        }

                        const hasOverlapAfterClamped = !!checkCabinetOverlap(
                            cabinetToMove,
                            cabinetToMove.position,
                            this.products,
                            -1
                        );

                        if (
                            (hasOverlapAfterClamped &&
                                newBasePosition.wasClamped) ||
                            !isPoseOnWalkableFloor(
                                cabinetToMove,
                                cabinetToMove.position
                            )
                        ) {
                            restorePreviousPose();
                        }
                    }
                }

                // Shape-only: other cabinets may legitimately sit in a wall.
                if (isNonRectShapeProduct(cabinetToMove)) {
                    const poseBeforeNudge = cabinetToMove.position.clone();
                    // Order matters: leave the neighbour first, then correct
                    // for walls. The reverse could push the panel back into it.
                    const bodyNudge = separateFromProducts(
                        cabinetToMove,
                        this.products,
                        isBoundsOnWalkableFloor
                    );
                    const wallNudge = clampGroupIntoRoomFootprint(
                        cabinetToMove,
                        wall
                    );

                    // The footprint clamp only retreats a shape that still fits
                    // a leg, so a nudge can still settle off-floor.
                    if (bodyNudge || wallNudge) {
                        if (isBoundsOnWalkableFloor(boxOf(cabinetToMove))) {
                            previousPosition.copy(cabinetToMove.position);
                        } else {
                            cabinetToMove.position.copy(poseBeforeNudge);
                            cabinetToMove.updateMatrixWorld(true);
                        }
                    }
                }
            } finally {
                this.isProcessingMouseMove = false;

                // Process any pending mouse move events
                if (this.pendingMouseMove) {
                    const pendingEvent = this.pendingMouseMove;
                    this.pendingMouseMove = null;
                    // Use setTimeout to prevent stack overflow and allow other events to process
                    setTimeout(() => {
                        onMouseMove(pendingEvent);
                    }, 0);
                }
            }
        }, 16); // Reduced throttle time for smoother interaction

        const onMouseUp = (event: MouseEvent) => {
            // Handle right-click mouse up
            if (event.button === 2) {
                this.endPanning('right', controls);
                return; // For right-click, we only handle panning, not other interactions
            }

            if (!this.selectedCabinet) {
                // Deselected mid-press (e.g. Escape while dragging): end the
                // drag, or its flags would keep blocking later clicks.
                if (isDragging) {
                    isDragging = false;
                    store.dispatch(setIsDragging(false));
                    controls.enabled = true;
                }
                this.isDragging = false;

                if (this.deselectedDragCabinet) {
                    this.dropDragGhosts(this.deselectedDragCabinet, scene);
                    autoSave(this.deselectedDragCabinet);
                    this.deselectedDragCabinet = null;
                }
                return;
            }

            // Handle side button clicks
            const sideButtonResult = handleSideButtonClick(
                event,
                camera,
                this.container,
                this.sideButtonRefs,
                this.selectedCabinet,
                this.sideButtonCallbacks,
                this.sideButtonMouseMoved
            );

            if (sideButtonResult) {
                // Side button was clicked - reset tracking, stop dragging, and exit early
                this.sideButtonMouseDown = null;
                this.sideButtonMouseMoved = false;
                this.sideButtonWasClicked = true; // Set flag to prevent cabinet selection
                isDragging = false; // Reset dragging state to prevent cabinet following cursor
                store.dispatch(setIsDragging(false));
                controls.enabled = true; // Re-enable controls
                return; // Exit early to prevent other click handling
            }

            // Reset tracking if we had mouse down but didn't click a button
            if (this.sideButtonMouseDown) {
                this.sideButtonMouseDown = null;
                this.sideButtonMouseMoved = false;
            }

            // Handle sprite click if we clicked on a sprite
            if (this.clickedSprite && this.clickedSprite.userData?.onClick) {
                // Update raycaster for mouse up position
                const dimension = this.container.getBoundingClientRect();
                const mouseUpX =
                    ((event.clientX - dimension.left) / dimension.width) * 2 -
                    1;
                const mouseUpY =
                    (-(event.clientY - dimension.top) / dimension.height) * 2 +
                    1;

                const mouseUpRaycaster = new THREE.Raycaster();
                mouseUpRaycaster.setFromCamera(
                    new THREE.Vector2(mouseUpX, mouseUpY),
                    camera
                );

                const movementArrowBlocksSpriteClick =
                    this.selectedCabinet &&
                    this.shouldPrioritizeMovementArrow(
                        mouseUpRaycaster,
                        scene,
                        this.selectedCabinet
                    );

                if (movementArrowBlocksSpriteClick) {
                    this.clickedSprite = null;
                } else {
                    // If the sprite is hovered (even if occluded), allow the click
                    const isStillHovered =
                        this.hoveredSprite === this.clickedSprite;

                    // Check if mouse up is also on the same sprite (for non-occluded clicks)
                    const mouseUpIntersects = mouseUpRaycaster.intersectObjects(
                        scene.children.filter((obj) => obj.visible === true),
                        true
                    );

                    const mouseUpClosest = mouseUpIntersects[0];

                    // Trigger click if:
                    // 1. The sprite is still hovered (works even when occluded), OR
                    // 2. Mouse up is on the same sprite (traditional behavior)
                    if (
                        isStillHovered ||
                        (mouseUpClosest &&
                            mouseUpClosest.object === this.clickedSprite)
                    ) {
                        (
                            this.clickedSprite.userData as {
                                onClick: (
                                    event: MouseEvent,
                                    raycaster: THREE.Raycaster,
                                    camera: THREE.Camera
                                ) => void;
                            }
                        ).onClick(event, mouseUpRaycaster, camera);

                        // Set flag to prevent cabinet selection in handleLeftClick
                        this.spriteWasClicked = true;
                    }

                    this.clickedSprite = null;
                    return;
                }
            }

            // If we were panning (right or left click), end panning and exit
            if (
                this.panningState.right.active ||
                this.panningState.left.active
            ) {
                this.endPanning('right', controls);
                this.endPanning('left', controls);
                // Reset dragging state if needed
                if (isDragging) {
                    isDragging = false;
                    store.dispatch(setIsDragging(false));
                }

                return;
            }

            let index: string;
            if (isNaN(Number(this.selectedCabinet.userData.id))) {
                [, index] = String(this.selectedCabinet.userData.id).split('-');
            }

            // Only reset dragging state if we actually were dragging (mouse moved)
            // This prevents flickering when just clicking (not dragging)
            if (isDragging && hasMouseMoved) {
                store.dispatch(setIsDragging(false));
            }
            isDragging = false;
            controls.enabled = true;
            this.triggerEvents(this.selectedCabinet, 'onClick', true);

            // take the previous state before resetting
            const prevDraggingState = this.isDragging;
            this.isDragging = false;
            // if (!this.selectedCabinet.userData.isSnapMode) {
            //     this.wasSnapped = false;
            // }

            this.dropDragGhosts(this.selectedCabinet, scene);
            if (!this.selectedCabinet.userData.isSnapMode) {
                this.selectedCabinet.userData.wasSnapped = false;
            }

            if (
                prevDraggingState &&
                this.selectedCabinet?.userData?.isPlaceholder
            ) {
                this.selectedCabinet.userData.savedPosition =
                    this.selectedCabinet.position.clone();
                this.setProducts(this.cabinetGroupListRef?.current ?? []);
            }

            // A drag can only start on the selected cabinet, so save whenever
            // one happened, including after it auto-rotated onto another
            // wall. Not gated on the URL check below: the rotation re-renders
            // the planner, which can leave the cached search params stale.
            if (prevDraggingState) {
                autoSave(this.selectedCabinet);
            }

            // Re-sync from the live URL rather than trusting the cached copy.
            this.searchParams = new URLSearchParams(window.location.search);
            const cabinetId = this.searchParams.get('selected');
            const selectedIndex = this.searchParams.get('index');
            const isSelected =
                cabinetId == this.selectedCabinet.userData.values.id &&
                (index == undefined || index == selectedIndex);

            if (isSelected) {
                // Only remove 'selected' parameter if edit sidebar is not open
                const isEditSidebarOpen = this.isEditSidebarOpen();
                if (!isEditSidebarOpen) {
                    // this.selectedCabinet.userData.selected = false;
                    this.searchParams.delete('selected');
                    this.searchParams.delete('index');
                    this.updateSearchParams(this.searchParams);
                }

                if (prevDraggingState) {
                    // Reselect the cabinet after dragging to restore highlight
                    // Use setTimeout to ensure this happens after the drag state is fully reset

                    if (this.selectedCabinet) {
                        // Reset movement mode for all cabinets before reselecting, excluding the current one
                        this.resetMovementModeForAllCabinets(
                            this.selectedCabinet
                        );

                        // Reselect the cabinet to restore highlight
                        selectCabinet(
                            this.selectedCabinet,
                            camera,
                            this.renderer
                        );

                        if (!this.selectedCabinet.userData?.isPlaceholder) {
                            store.dispatch(
                                setSelectedProductValue(
                                    this.selectedCabinet.userData.values
                                )
                            );
                        }

                        if (this.selectedCabinet.userData?.isPlaceholder) {
                            this.searchParams.set(
                                'selected',
                                String(this.selectedCabinet.userData.id)
                            );
                            if (index !== undefined) {
                                this.searchParams.set('index', index);
                            }
                            this.updateSearchParams(this.searchParams);
                        } else {
                            const values = this.selectedCabinet.userData.values;
                            if (values) {
                                this.searchParams.set(
                                    'selected',
                                    values.job_cabinet_id?.toString() || ''
                                );
                                if (index !== undefined) {
                                    this.searchParams.set('index', index);
                                }
                                this.updateSearchParams(this.searchParams);
                            }
                        }

                        // Update selected cabinet group in context
                        if (this.setSelectedCabinetGroup) {
                            this.setSelectedCabinetGroup(this.selectedCabinet);
                        }

                        const {isFillerProduct, isVerminProduct} =
                            this.selectedCabinet?.userData;
                        if (isFillerProduct || isVerminProduct) {
                            handleFillerSwitching(
                                this.ghostCabinet
                                    ? [...this.products, this.ghostCabinet]
                                    : this.products,
                                wall.children,
                                this.selectedCabinet
                            );
                        }
                    }
                }
            }

            // Use debounced overlap check for better performance
            this.debouncedOverlapCheck(
                this.selectedCabinet,
                this.selectedCabinet?.userData.newBasePosition,
                roomType
            );
        };

        // Keep the browser context menu off the canvas (right-drag pans).
        const handleRightClick = (event: MouseEvent) => {
            event.preventDefault();
        };

        const handleSelect = debounce(() => {
            return new Promise<CabinetMesh | null>((resolve) => {
                const previouslySelected = this.selectedCabinet;
                this.checkIntersections((selectedCabinet) => {
                    const handleSelect = () => {
                        // Set current wall based on wall under mouse pointer at click time
                        this.setCurrentWallFromMouse(scene, raycaster);

                        if (selectedCabinet.userData.isTempMesh) {
                            this.selectedCabinet = null;
                            return;
                        }
                        const jobCabinetId =
                            selectedCabinet.userData?.values?.job_cabinet_id;
                        const numericJobCabinetId = Number(jobCabinetId);
                        // Pending duplicates use -1 until the API returns a real id.
                        // Hashed preview ids become NaN and must remain selectable.
                        if (
                            !selectedCabinet.userData?.isPlaceholder &&
                            !Number.isNaN(numericJobCabinetId) &&
                            numericJobCabinetId < 0
                        ) {
                            this.selectedCabinet = null;
                            return;
                        }
                        resolve(selectedCabinet);

                        let index: string | undefined;
                        if (isNaN(Number(selectedCabinet.userData.id))) {
                            [, index] = String(
                                selectedCabinet.userData.id
                            ).split('-');
                        }

                        if (previouslySelected?.uuid !== selectedCabinet.uuid) {
                            this.clearCabinetHighlight(previouslySelected);
                        }

                        // Reset movement mode for all cabinets before selecting new one, excluding the new selection
                        this.resetMovementModeForAllCabinets(selectedCabinet);

                        if (this.setSelectedCabinetGroup) {
                            this.setSelectedCabinetGroup(selectedCabinet);
                        }
                        selectCabinet(selectedCabinet, camera, this.renderer);
                        if (!selectedCabinet.userData?.isPlaceholder) {
                            store.dispatch(
                                setSelectedProductValue(
                                    selectedCabinet.userData.values
                                )
                            );
                        }

                        const values = selectedCabinet.userData.values;
                        if (selectedCabinet.userData?.isPlaceholder) {
                            this.searchParams.set(
                                'selected',
                                String(selectedCabinet.userData.id || '')
                            );
                            if (index !== undefined) {
                                this.searchParams.set('index', index);
                            }
                            this.updateSearchParams(this.searchParams);

                            if (this.isRoomPlannerEditing) {
                                const placeholderId = String(
                                    selectedCabinet.userData.id ?? ''
                                );
                                if (
                                    this.searchParams.get('placeholderEdit') !==
                                    placeholderId
                                ) {
                                    /*
                                     * Discarding is already settled before
                                     * handleSelect runs, so swap the editor
                                     * over without a second prompt.
                                     */
                                    this.updateSearchParams(
                                        buildPlaceholderEditorParams(
                                            this.searchParams,
                                            selectedCabinet
                                        )
                                    );
                                }
                            }
                        } else if (values) {
                            // Re-sync from the live URL rather than trusting
                            // the cached copy: a save that just completed
                            // (e.g. via the "Save Changes" dialog) may have
                            // cleared cabinetId/product out from under it
                            // while this selection was in flight.
                            this.searchParams = new URLSearchParams(
                                window.location.search
                            );
                            this.searchParams.set(
                                'selected',
                                values.job_cabinet_id?.toString() || ''
                            );
                            if (index !== undefined) {
                                this.searchParams.set('index', index);
                            }
                            this.updateSearchParams(this.searchParams);

                            const isCabinetEditorOpen =
                                this.isRoomPlannerEditing ||
                                (this.searchParams.get('preventReset') ===
                                    'true' &&
                                    this.searchParams.has('cabinetId'));

                            if (isCabinetEditorOpen) {
                                this.searchParams.delete('placeholderEdit');
                                this.searchParams.delete('placeholderFace');
                                this.searchParams.delete('placeholderName');
                                this.searchParams.delete('category');
                                this.searchParams.delete('subCategory');
                                this.searchParams.set(
                                    'cabinetId',
                                    values.job_cabinet_id?.toString() || ''
                                );
                                this.searchParams.set(
                                    'product',
                                    values.type?.toString() || ''
                                );
                                this.searchParams.set('preventReset', 'true');
                                this.updateSearchParams(this.searchParams);
                                store.dispatch(setIsRoomPlannerEditing(true));
                            }

                            store.dispatch(setBackupProductValues(values));
                        }

                        this.revalidateOverlaps();
                    };

                    const editingPlaceholder =
                        this.searchParams?.has('placeholderEdit') ?? false;
                    const isReselectingCurrentEdit = isReselectingEditedCabinet(
                        this.searchParams?.get('cabinetId'),
                        selectedCabinet.userData?.values?.job_cabinet_id
                    );

                    if (
                        !isReselectingCurrentEdit &&
                        this.hasUnsavedProductChanges()
                    ) {
                        this.confirmDiscardProductChanges(() => {
                            handleSelect();
                        }, editingPlaceholder);
                    } else {
                        handleSelect();
                    }
                });
            });
        }, 20);

        // Track if mouse has moved to prevent click on release
        let hasMouseMoved = false;

        // Where the current left press / touch started. hasMouseMoved is
        // measured from it but only while a cabinet is selected, so the
        // click check uses it as well to catch camera rotation: releasing a
        // rotation over a cabinet must not select it.
        const CLICK_MOVE_THRESHOLD = 5;
        const pressStart = {x: 0, y: 0};
        const setPressStart = (clientX: number, clientY: number) => {
            pressStart.x = clientX;
            pressStart.y = clientY;
        };

        const handleLeftClick = (event: MouseEvent) => {
            event.preventDefault();

            const showCabinets = store.getState().roomPlanner.showCabinets;
            if (!showCabinets) return;

            // Prevent click if mouse moved or we were dragging
            if (hasMouseMoved || isDragging) {
                this.revalidateOverlaps();
                return;
            }

            // If we were panning, don't do anything (prevent deselection)
            if (
                this.checkAndResetWasPanning('right') ||
                this.checkAndResetWasPanning('left')
            ) {
                return;
            }

            // The pointer travelled (e.g. rotating the camera), so this
            // release isn't a click on whatever ended up under the cursor.
            if (
                Math.hypot(
                    event.clientX - pressStart.x,
                    event.clientY - pressStart.y
                ) > CLICK_MOVE_THRESHOLD
            ) {
                return;
            }

            // Revalidate overlaps when deselecting on one click outside (not panning)
            if (!hasMouseMoved && this.selectedCabinet) {
                this.revalidateOverlaps();
            }

            // If we clicked on a sprite, don't proceed with cabinet selection
            // This prevents changing the active cabinet when clicking on distance measurements
            if (this.spriteWasClicked) {
                this.spriteWasClicked = false; // Reset flag
                return;
            }

            // If we clicked on a side button, don't proceed with cabinet selection
            // This prevents changing the active cabinet when clicking on side buttons
            if (this.sideButtonWasClicked) {
                this.sideButtonWasClicked = false; // Reset flag
                return;
            }

            // Prevent interaction if quick add is still pending
            if (this.selectedCabinet?.userData?.isQuickAddPending) {
                return;
            }

            let index: string;
            if (isNaN(Number(this.selectedCabinet?.userData.id))) {
                [, index] = String(this.selectedCabinet?.userData.id).split(
                    '-'
                );
            }

            const dimension = this.container.getBoundingClientRect();
            mouse.x =
                ((event.clientX - dimension.left) / dimension.width) * 2 - 1;
            mouse.y =
                (-(event.clientY - dimension.top) / dimension.height) * 2 + 1;

            raycaster.setFromCamera(mouse, camera);

            // Perform raycasting to detect intersected objects
            const intersects = raycaster.intersectObjects(
                this.getLayoutMeshes(),
                true
            );
            this.setIntersections(intersects);

            if (this.selectedCabinet?.userData?.isMovementMode) {
                const arrowHandled = this.handleArrowIntersections(
                    event,
                    raycaster,
                    camera,
                    scene,
                    this.selectedCabinet,
                    index
                );
                // If arrow was handled, exit early to keep the active group
                if (arrowHandled) {
                    return;
                }
            }

            void handleSelect()?.then((selectedCabinet: CabinetMesh | null) => {
                if (selectedCabinet) {
                    // Read the live URL rather than the cached copy: this
                    // fires after handleSelect()'s own dialog-confirm-then-save
                    // await, and this.searchParams is only guaranteed fresh by
                    // implicit ordering with that code, not by anything this
                    // callback itself checks.
                    this.searchParams = new URLSearchParams(
                        window.location.search
                    );

                    // Check if edit sidebar is open before proceeding with cabinet selection/deselection
                    const isEditSidebarOpen = this.isEditSidebarOpen();

                    // Only proceed with cabinet selection/deselection if we didn't click an arrow
                    if (!isEditSidebarOpen) {
                        this.searchParams.delete('selected');
                        this.searchParams.delete('index');
                        this.updateSearchParams(this.searchParams);
                    }

                    this.handleSceneIntersections(
                        raycaster,
                        camera,
                        scene,
                        selectedCabinet
                    );
                }
            });
        };

        const domElement = this.renderer?.domElement;

        // Combined mousedown handler for both left and right clicks
        const onMouseDownCombined = (event: MouseEvent) => {
            if (event.button === 2) {
                // Right-click - handle panning
                onRightMouseDown(event);
            } else if (event.button === 0) {
                // Left-click - handle normal interaction
                setPressStart(event.clientX, event.clientY);
                onMouseDown(event);
            }
        };

        const MOVE_THRESHOLD = 8;
        const touchStartPos: {
            x?: number;
            y?: number;
        } = {};

        // Set once a second finger lands, until every finger has lifted. A
        // multi-finger touch pans / zooms the camera, so it must not drag,
        // select or deselect cabinets.
        let isMultiTouch = false;

        // Ends the first finger's press as if it lifted, so a cabinet drag
        // it started is dropped and saved and the camera controls come back
        // on. Its side button or distance label must not count as clicked.
        const endTouchPress = (touch: Touch) => {
            onMouseMove.cancel();
            this.sideButtonMouseMoved = true;
            this.clickedSprite = null;
            onMouseUp(
                new MouseEvent('mouseup', {
                    clientX: touch.clientX,
                    clientY: touch.clientY,
                    button: 0,
                })
            );
        };

        const eventListeners = {
            mousedown: onMouseDownCombined,
            mousemove: onMouseMove,
            mouseup: onMouseUp,
            contextmenu: handleRightClick,
            click: handleLeftClick,
            touchstart: (event: TouchEvent) => {
                event.preventDefault();

                if (event.touches.length > 1) {
                    if (!isMultiTouch) {
                        isMultiTouch = true;
                        endTouchPress(event.touches[0]);
                    }
                    return;
                }
                // A new gesture, even if the last one's end was missed.
                isMultiTouch = false;

                const touch = event.touches[0];
                touchStartPos.x = touch.clientX;
                touchStartPos.y = touch.clientY;
                setPressStart(touch.clientX, touch.clientY);

                const mouseEvent = new MouseEvent('mousedown', {
                    clientX: touch.clientX,
                    clientY: touch.clientY,
                    button: 0,
                });

                onMouseDown(mouseEvent);
            },
            touchmove: (event: TouchEvent) => {
                event.preventDefault();
                if (isMultiTouch) return;

                const touch = event.touches[0];
                const mouseEvent = new MouseEvent('mousemove', {
                    clientX: touch.clientX,
                    clientY: touch.clientY,
                });

                const deltaX = touch.clientX - touchStartPos.x;
                const deltaY = touch.clientY - touchStartPos.y;
                const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

                if (distance < MOVE_THRESHOLD) {
                    // Ignore micro-movement
                    return;
                }

                onMouseMove(mouseEvent);
            },
            touchend: (event: TouchEvent) => {
                event.preventDefault();
                if (isMultiTouch) {
                    // The press already ended when the second finger landed.
                    if (event.touches.length === 0) isMultiTouch = false;
                    return;
                }

                const touch = event.changedTouches[0];
                const mouseEvent = new MouseEvent('click', {
                    clientX: touch.clientX,
                    clientY: touch.clientY,
                });

                // Always trigger click for selection
                onMouseUp(mouseEvent);
                handleLeftClick(mouseEvent);
            },
        } as const;

        this.resetAttachPointerState = () => {
            hasMouseMoved = false;
            isDragging = false;
            controls.enabled = true;
        };

        Object.entries(eventListeners).forEach(([event, handler]) => {
            domElement?.addEventListener(event, handler);
        });

        this.boundDomElement = domElement ?? null;
        this.eventHandlers.set(CANVAS_EVENTS_KEY, eventListeners);
    }

    /**
     * Removes event listeners from a specific set of event handlers.
     * @private
     * @param {Events} events - Event handlers to remove.
     * @param {HTMLElement} domElement - DOM element to remove events from.
     */
    private removeEventListeners(events: Events, domElement: HTMLElement) {
        Object.entries(events).forEach(([event, handler]) => {
            if (event && handler) {
                domElement?.removeEventListener(
                    event,
                    handler as EventListener
                );
            }
        });
    }

    /**
     * Clears all event listeners attached to the renderer's DOM element.
     */
    public clearEvents() {
        this.resetAttachPointerState = null;
        const el = this.boundDomElement ?? this.renderer?.domElement;
        if (this.eventHandlers.size > 0 && el) {
            this.eventHandlers.forEach((events) => {
                this.removeEventListeners(events, el);
            });
            this.eventHandlers.clear();
        }
        this.boundDomElement = null;
    }

    /**
     * Clear event listeners for a specific cabinet.
     * @param {string} key - The key of the event to clear.
     */
    public clearEvent(key: string) {
        if (this.eventHandlers.has(key)) {
            const events = this.eventHandlers.get(key);
            if (!events) return;

            this.removeEventListeners(events, this.renderer.domElement);
            this.eventHandlers.delete(key);
        }
    }

    /**
     * Checks if any events are already attached.
     * @return {boolean} - True if any events are attached, false otherwise.
     */
    public areEventsAttached(): boolean {
        return this.eventHandlers.size > 0;
    }
}

export default new ProductEvents();
